/**
 * Minimal runtime conformance checks for contract responses.
 *
 * The console is a *consumer* of the frozen contract: if the façade ever answers
 * with something that does not match `openapi/openapi.yaml`, the UI must fail
 * loudly as a `contract` error instead of rendering `undefined` and looking
 * healthy. Only the fields the UI actually reads are validated — this is not a
 * JSON Schema validator, and it deliberately rejects unknown `problem.code`
 * values so a drift in the error catalogue becomes visible.
 */

import { ApiError } from './errors';
import {
  CONFIG_SECTION_STATUSES,
  REPORT_COLUMN_KINDS,
  REPORT_FILTER_KINDS,
  REPORT_FORMATS,
  PROBLEM_CODES,
  ACTIVATION_STATUSES,
  ALARM_SEVERITIES,
  ALARM_STATES,
  CAMPAIGN_ACTIONS,
  CAMPAIGN_KINDS,
  CAMPAIGN_STATUSES,
  CAMPAIGN_WINDOW_STATUSES,
  MAINTENANCE_SCOPES,
  MAINTENANCE_STATES,
  type ActionResponse,
  type ActivationAccepted,
  type ActivationStatus,
  type ActivationStep,
  type Alarm,
  type AlarmAckResponse,
  type AlarmAssignResponse,
  type AlarmClearResponse,
  type AlarmEscalateResponse,
  type AlarmSeverity,
  type AlarmState,
  type AlarmStats,
  type AlarmsResponse,
  type AuditEntry,
  type AuditListResponse,
  type BindingResponse,
  type Campaign,
  type CampaignAccepted,
  type CampaignFilter,
  type CampaignHalted,
  type CampaignListItem,
  type CampaignListResponse,
  type CampaignNextAction,
  type CampaignProgress,
  type CampaignRolledBack,
  type CampaignScheduled,
  type CampaignWindow,
  type CampaignWave,
  type CampaignWaveInfo,
  type CloudEvent,
  type DecommissionResponse,
  type DeletedResponse,
  type DeviceStats,
  type DeviceSummary,
  type DevicesListResponse,
  type DiscoverAccepted,
  type ReplaceAccepted,
  type TagsResponse,
  type EventReadResponse,
  type Incident,
  type ConfigApplied,
  type ConfigBundle,
  type ConfigChange,
  type ConfigProblemLine,
  type ConfigRestorePointList,
  type ConfigSectionReport,
  type ConfigSectionStatus,
  type ConfigValidation,
  type ReportCatalog,
  type ReportColumn,
  type ReportDownload,
  type ReportFilterDef,
  type ReportFormat,
  type ReportInfo,
  type ReportResult,
  type IncidentAttachResponse,
  type IncidentCorrelateResponse,
  type IncidentGroup,
  type IncidentListResponse,
  type IncidentTimelineEntry,
  type NotificationChannel,
  type NotificationChannelDeletedResponse,
  type NotificationChannelListResponse,
  type NotificationDelivery,
  type NotificationDeliveryListResponse,
  type MaintenanceCancelResponse,
  type MaintenanceCreateResponse,
  type MaintenanceListResponse,
  type PresetWriteResponse,
  type ReconciliationListResponse,
  type TemplateListResponse,
  type TotpEnrollment,
  type TotpReset,
  type OnuDetailResponse,
  type MacTableResponse,
  type MacTableEntry,
  type OnuRebootResponse,
  type OnuBulkTargetResult,
  type OnuBulkAuthorizeResponse,
  type PonPortRow,
  type PonPortsResponse,
  type BackupEntry,
  type BackupList,
  type BackupVerdict,
  type LegalHold,
  type LegalHoldListResponse,
  type LegalHoldLiftResponse,
  type LegalHoldPlaceResponse,
  type MigrationApplied,
  type MigrationStatus,
  type MigrationStep,
  type MigrationsApplyAccepted,
  type OutboxListResponse,
  type OutboxRedriveAccepted,
  type OutboxRow,
  type RetentionRunAccepted,
  type MaintenanceScope,
  type MaintenanceState,
  type MaintenanceWindow,
  type FirmwareFile,
  type FirmwareListResponse,
  type FirmwareUploadAccepted,
  type Health,
  type Job,
  type JobListRow,
  type JobsListResponse,
  type OperationRunAccepted,
  type OperationSlotItem,
  type OperationValuePathEntry,
  type OperationValueResponse,
  type OperationsManifestBlock,
  type OperationsResponse,
  type OperationWriteAccepted,
  type OnuAuthorizeResponse,
  type OnuTableResponse,
  type OnuTableRow,
  type OltInventoryItem,
  type OltListResponse,
  type OpticalRead,
  type ParameterItem,
  type ParamsResponse,
  type Problem,
  type ProblemCode,
  type ProfileActivationAccepted,
  type ProfileDiffEntry,
  type ProfileDocument,
  type ProfileProblemReport,
  type ProfileReadResponse,
  type ProfileReadinessResponse,
  type ProfileValidationReport,
  type ProfileVersionListResponse,
  type ProfileVersionRow,
  type SessionGranted,
  type SessionRevoked,
  type ServiceProfile,
  type ServiceProfileDba,
  type ServiceProfileListResponse,
  type SigningKeyListResponse,
  type SigningKeyMetadata,
  type Subscriber,
  type SubscriberBindingView,
  type SubscriberDeviceView,
  type SubscriberListResponse,
  type SubscriberOverview,
  type SubscriberSearchHit,
  type SubscriberSearchResponse,
  type EffectivePermissions,
  type CapabilityWire,
  type StationItem,
  type StationsResponse,
  type ConsoleUser,
  type OpsSettingsRead,
  type UserListResponse,
  type UserMutationAccepted,
  type MatrixCellChange,
  type MatrixMutationAccepted,
  type SystemComponent,
  type SystemComponentDetail,
  type SystemComponentsResponse,
  type SystemQueuesResponse,
  type SystemWorkerBeat,
  type SystemWorkersResponse,
  type WorkerBeatState,
  type PermissionMatrixRead,
  USER_STATUSES,
  OPTICAL_WINDOWS,
  type OpticalDegrading,
  type OpticalDegradingDevice,
  type OpticalDegradingWindow,
  type OpticalHistory,
  type OpticalHistoryPoint,
  type OpticalRxBin,
  type OpticalWorst,
  type OpticalWorstDevice,
  type OpticalWindow,
  type PonCapacityResponse,
  type PonCapacityRow,
  DEGRADING_HORIZON_DAYS,
  DEGRADING_MIN_SAMPLES_PER_HALF,
  DEGRADING_WINDOWS,
} from './types';

const PROBLEM_CODE_SET: ReadonlySet<string> = new Set<string>(PROBLEM_CODES);

function fail(what: string, detail: string): never {
  throw new ApiError('contract', `${what}: ${detail}`);
}

function asRecord(value: unknown, what: string): Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    fail(what, `expected an object, got ${Array.isArray(value) ? 'array' : typeof value}`);
  }
  return value as Record<string, unknown>;
}

function str(obj: Record<string, unknown>, key: string, what: string): string {
  const v = obj[key];
  if (typeof v !== 'string') fail(what, `field "${key}" must be a string`);
  return v;
}

function strOrNull(obj: Record<string, unknown>, key: string, what: string): string | null {
  const v = obj[key];
  if (v === null || v === undefined) return null;
  if (typeof v !== 'string') fail(what, `field "${key}" must be a string or null`);
  return v;
}

function bool(obj: Record<string, unknown>, key: string, what: string): boolean {
  const v = obj[key];
  if (typeof v !== 'boolean') fail(what, `field "${key}" must be a boolean`);
  return v;
}

function num(obj: Record<string, unknown>, key: string, what: string): number {
  const v = obj[key];
  if (typeof v !== 'number' || !Number.isFinite(v)) {
    fail(what, `field "${key}" must be a number`);
  }
  return v;
}

function numOrNull(obj: Record<string, unknown>, key: string, what: string): number | null {
  const v = obj[key];
  if (v === null || v === undefined) return null;
  if (typeof v !== 'number' || !Number.isFinite(v)) {
    fail(what, `field "${key}" must be a number or null`);
  }
  return v;
}

function arr(obj: Record<string, unknown>, key: string, what: string): unknown[] {
  const v = obj[key];
  if (!Array.isArray(v)) fail(what, `field "${key}" must be an array`);
  return v;
}

/** `next_cursor` is `nullable: true` on every paginated response. */
function cursor(obj: Record<string, unknown>, what: string): string | null {
  const v = obj.next_cursor;
  if (v === null || v === undefined) return null;
  if (typeof v !== 'string') fail(what, 'field "next_cursor" must be a string or null');
  return v;
}

/** Closed enum check keeps a widened server enum from silently typing through. */
function oneOf(
  obj: Record<string, unknown>,
  key: string,
  allowed: readonly string[],
  what: string,
): string {
  const v = str(obj, key, what);
  if (!allowed.includes(v)) {
    fail(what, `field "${key}" must be one of ${allowed.join('|')}, got "${v}"`);
  }
  return v;
}

export function parseHealth(value: unknown): Health {
  const o = asRecord(value, 'Health');
  const what = 'Health';
  return {
    service: oneOf(o, 'service', ['acs-api'], what) as 'acs-api',
    version: str(o, 'version', what),
    status: oneOf(o, 'status', ['ok'], what) as 'ok',
    store_backend: oneOf(o, 'store_backend', ['memory', 'mongo'], what) as Health['store_backend'],
    nbi_mode: oneOf(o, 'nbi_mode', ['canned', 'http'], what) as Health['nbi_mode'],
    contract_version: oneOf(o, 'contract_version', ['v1'], what) as 'v1',
    time: str(o, 'time', what),
  };
}

export function parseDeviceSummary(value: unknown): DeviceSummary {
  const o = asRecord(value, 'DeviceSummary');
  const what = 'DeviceSummary';
  return {
    acs_device_id: str(o, 'acs_device_id', what),
    nas_ref: strOrNull(o, 'nas_ref', what),
    serial: str(o, 'serial', what),
    oui: str(o, 'oui', what),
    product_class: str(o, 'product_class', what),
    manufacturer: str(o, 'manufacturer', what),
    model: str(o, 'model', what),
    software_version: str(o, 'software_version', what),
    online: bool(o, 'online', what),
    last_inform_at: strOrNull(o, 'last_inform_at', what),
    binding_version: num(o, 'binding_version', what),
    stale: bool(o, 'stale', what),
  };
}

export function parseDevicesList(value: unknown): DevicesListResponse {
  const o = asRecord(value, 'DevicesListResponse');
  const what = 'DevicesListResponse';
  return {
    items: arr(o, 'items', what).map((i) => parseDeviceSummary(i)),
    next_cursor: cursor(o, what),
  };
}

/**
 * `GET /ops/v1/stats/devices` — four counts and the time they were taken.
 *
 * Every field is required: a body without `offline` is a body the server did not write, and
 * deriving it here would put a client-side arithmetic where the door's own answer belongs.
 */
export function parseDeviceStats(value: unknown): DeviceStats {
  const o = asRecord(value, 'DeviceStats');
  const what = 'DeviceStats';
  return {
    total: num(o, 'total', what),
    online: num(o, 'online', what),
    offline: num(o, 'offline', what),
    unbound: num(o, 'unbound', what),
    counted_at: str(o, 'counted_at', what),
  };
}

/** A closed count map: every word of the alphabet must be there, and nothing else may be. */
function countMap(
  obj: Record<string, unknown>,
  key: string,
  alphabet: readonly string[],
  what: string,
): Record<string, number> {
  const record = asRecord(obj[key], `${what}.${key}`);
  const extra = Object.keys(record).filter((word) => !alphabet.includes(word));
  if (extra.length > 0) {
    fail(what, `${key} carries word(s) outside the contract's alphabet: ${extra.join(', ')}`);
  }
  const out: Record<string, number> = {};
  for (const word of alphabet) out[word] = num(record, word, `${what}.${key}`);
  return out;
}

/**
 * `GET /ops/v1/stats/alarms` — the lifecycle counts over the whole store.
 *
 * The two maps are the closed vocabularies (`ALARM_STATES`, `ALARM_SEVERITIES`), and both are
 * validated as such: a word the console does not know would otherwise render as an empty cell,
 * and a missing word would be a zero the server never counted.
 */
export function parseAlarmStats(value: unknown): AlarmStats {
  const o = asRecord(value, 'AlarmStats');
  const what = 'AlarmStats';
  return {
    total: num(o, 'total', what),
    active: num(o, 'active', what),
    by_state: countMap(o, 'by_state', ALARM_STATES, what) as Record<AlarmState, number>,
    active_by_severity: countMap(o, 'active_by_severity', ALARM_SEVERITIES, what) as Record<
      AlarmSeverity,
      number
    >,
    counted_at: str(o, 'counted_at', what),
  };
}

export function parseParams(value: unknown): ParamsResponse {
  const o = asRecord(value, 'ParamsResponse');
  const what = 'ParamsResponse';
  return {
    items: arr(o, 'items', what).map((raw): ParameterItem => {
      const p = asRecord(raw, 'ParameterItem');
      return {
        path: str(p, 'path', 'ParameterItem'),
        value: p.value,
        type: str(p, 'type', 'ParameterItem'),
        writable: bool(p, 'writable', 'ParameterItem'),
        captured_at: strOrNull(p, 'captured_at', 'ParameterItem'),
      };
    }),
    next_cursor: cursor(o, what),
    source_version: str(o, 'source_version', what),
    stale: bool(o, 'stale', what),
  };
}

export function parseStations(value: unknown): StationsResponse {
  const o = asRecord(value, 'StationsResponse');
  const what = 'StationsResponse';
  return {
    items: arr(o, 'items', what).map((raw): StationItem => {
      const s = asRecord(raw, 'StationItem');
      return {
        mac: str(s, 'mac', 'StationItem'),
        ip: strOrNull(s, 'ip', 'StationItem'),
        hostname: strOrNull(s, 'hostname', 'StationItem'),
        ssid_instance: numOrNull(s, 'ssid_instance', 'StationItem'),
        seen_at: str(s, 'seen_at', 'StationItem'),
      };
    }),
    next_cursor: cursor(o, what),
    stale: bool(o, 'stale', what),
  };
}

export function parseJob(value: unknown): Job {
  const o = asRecord(value, 'Job');
  const what = 'Job';
  return {
    job_id: str(o, 'job_id', what),
    action: str(o, 'action', what),
    status: str(o, 'status', what),
    result: o.result,
    started_at: strOrNull(o, 'started_at', what),
    completed_at: strOrNull(o, 'completed_at', what),
    requested_at: strOrNull(o, 'requested_at', what),
  };
}

export function parseActionResponse(value: unknown): ActionResponse {
  const o = asRecord(value, 'ActionResponse');
  const what = 'ActionResponse';
  return {
    job_id: str(o, 'job_id', what),
    status: str(o, 'status', what),
  };
}

export function parseBindingResponse(value: unknown): BindingResponse {
  const o = asRecord(value, 'BindingResponse');
  const what = 'BindingResponse';
  return { bound: true, binding_version: num(o, 'binding_version', what) };
}

/** `ActivationAccepted` — the 202 body of `POST /ops/v1/activations`. */
export function parseActivationAccepted(value: unknown): ActivationAccepted {
  const o = asRecord(value, 'ActivationAccepted');
  const what = 'ActivationAccepted';
  return {
    activation_id: str(o, 'activation_id', what),
    status: oneOf(o, 'status', ['await_inform'], what) as 'await_inform',
  };
}

/**
 * One recorded adapter step. `steps.items` is `{ type: object }` in the
 * contract, so only `step`/`status` are pinned; the rest (per-step `result`,
 * `error`, `at`) pass through untouched for the stepper to render.
 */
function parseActivationStep(value: unknown): ActivationStep {
  const s = asRecord(value, 'ActivationStep');
  const what = 'ActivationStep';
  const detail = s.detail;
  return {
    step: str(s, 'step', what),
    status: str(s, 'status', what),
    ...(typeof detail === 'string' ? { detail } : {}),
    ...omitUndefined({
      at: typeof s.at === 'string' ? s.at : undefined,
      result: s.result,
      error: s.error === undefined || s.error === null ? undefined : asRecord(s.error, what),
      best_effort: typeof s.best_effort === 'boolean' ? s.best_effort : undefined,
      note: typeof s.note === 'string' ? s.note : undefined,
    }),
  };
}

/** `{step, code, detail}`; the server answers `{}` when a failure predates any step. */
function parseActivationError(raw: unknown): { step: string; code: string; detail: string } | null {
  const o = asRecord(raw, 'ActivationStatus.error');
  const step = typeof o.step === 'string' ? o.step : '';
  const code = typeof o.code === 'string' ? o.code : '';
  const detail = typeof o.detail === 'string' ? o.detail : '';
  if (step === '' && code === '' && detail === '') return null;
  return { step, code, detail };
}

function omitUndefined<T extends Record<string, unknown>>(value: T): Partial<T> {
  const out: Record<string, unknown> = {};
  for (const [key, entry] of Object.entries(value)) {
    if (entry !== undefined) out[key] = entry;
  }
  return out as Partial<T>;
}

/** `ActivationStatus` — the read-back of `GET /ops/v1/activations/{id}`. */
export function parseActivationStatus(value: unknown): ActivationStatus {
  const o = asRecord(value, 'ActivationStatus');
  const what = 'ActivationStatus';
  const acsDeviceId = o.acs_device_id;
  const error = o.error;
  const parsedError = error === undefined || error === null ? null : parseActivationError(error);
  return {
    activation_id: str(o, 'activation_id', what),
    status: oneOf(o, 'status', ACTIVATION_STATUSES, what) as ActivationStatus['status'],
    steps: arr(o, 'steps', what).map((raw) => parseActivationStep(raw)),
    ...(typeof acsDeviceId === 'string' ? { acs_device_id: acsDeviceId } : {}),
    ...(parsedError === null ? {} : { error: parsedError }),
  };
}

export function parseEvents(value: unknown): EventReadResponse {
  const o = asRecord(value, 'EventReadResponse');
  const what = 'EventReadResponse';
  return {
    items: arr(o, 'items', what).map((raw): CloudEvent => {
      const e = asRecord(raw, 'CloudEvent');
      const type = str(e, 'type', 'CloudEvent');
      if (!type.startsWith('io.wicore.acs.')) {
        fail('CloudEvent', `type must start with "io.wicore.acs.", got "${type}"`);
      }
      const datacontenttype = e.datacontenttype;
      return {
        specversion: oneOf(e, 'specversion', ['1.0'], 'CloudEvent') as '1.0',
        id: str(e, 'id', 'CloudEvent'),
        source: str(e, 'source', 'CloudEvent'),
        type,
        time: str(e, 'time', 'CloudEvent'),
        ...(datacontenttype === undefined
          ? {}
          : { datacontenttype: str(e, 'datacontenttype', 'CloudEvent') }),
        data: asRecord(e.data, 'CloudEvent.data'),
      };
    }),
    next_cursor: cursor(o, what),
  };
}

/* ------------------------------------------------------------------ */
/* Alarms — `GET /ops/v1/alarms` + `POST …/alarms/{event_id}:ack`      */
/* ------------------------------------------------------------------ */

/**
 * One `Alarm` row. `severity` is pinned to the contract enum (a widened server
 * severity must become a visible `contract` error rather than an unstyled row),
 * while `type` stays free text because `models.Alarm.type` is a plain string and
 * a new trap kind must never drop a row off the feed.
 */
export function parseAlarm(value: unknown): Alarm {
  const o = asRecord(value, 'Alarm');
  const what = 'Alarm';
  const data = o.data;
  return {
    alarm_id: str(o, 'alarm_id', what),
    type: str(o, 'type', what),
    severity: oneOf(o, 'severity', ALARM_SEVERITIES, what) as AlarmSeverity,
    acs_device_id: str(o, 'acs_device_id', what),
    time: str(o, 'time', what),
    data: data === undefined || data === null ? {} : asRecord(data, `${what}.data`),
    acked: bool(o, 'acked', what),
    // The lifecycle is READ, never guessed: a body without `state` is a
    // contract error, because defaulting it to 'open' would show the console
    // a state the server did not send.
    state: oneOf(o, 'state', ALARM_STATES, what) as AlarmState,
    // N3 (G-ALM-04): the dedup facets, OPTIONAL — rows born before N3 carry
    // none, and a field the server did not send must not be invented.
    fingerprint: o.fingerprint === undefined ? undefined : str(o, 'fingerprint', what),
    occurrences: o.occurrences === undefined ? undefined : num(o, 'occurrences', what),
    last_seen_at: strOrNull(o, 'last_seen_at', what),
    escalated: bool(o, 'escalated', what),
    assignee: strOrNull(o, 'assignee', what),
  };
}

export function parseAlarms(value: unknown): AlarmsResponse {
  const o = asRecord(value, 'AlarmsResponse');
  const what = 'AlarmsResponse';
  return {
    items: arr(o, 'items', what).map((raw) => parseAlarm(raw)),
    next_cursor: cursor(o, what),
  };
}

/**
 * `acked` is `{ const: true }` in the contract, so the field is not re-read (the
 * same call `parseBindingResponse` makes for `bound`). `already_acked` is the
 * interesting one: the audit ledger dedupes per alarm + client.
 */
export function parseAlarmAckResponse(value: unknown): AlarmAckResponse {
  const o = asRecord(value, 'AlarmAckResponse');
  const what = 'AlarmAckResponse';
  return {
    alarm_id: str(o, 'alarm_id', what),
    acked: true,
    already_acked: bool(o, 'already_acked', what),
    note: strOrNull(o, 'note', what),
    acked_by: str(o, 'acked_by', what),
  };
}

/**
 * The three lifecycle answers (N2). Each carries the person who acted — the
 * doors refuse a machine assertion (403 `actor-not-human`), so a name here is
 * always a name and never a client id.
 */
export function parseAlarmClearResponse(value: unknown): AlarmClearResponse {
  const o = asRecord(value, 'AlarmClearResponse');
  const what = 'AlarmClearResponse';
  return {
    alarm_id: str(o, 'alarm_id', what),
    state: str(o, 'state', what),
    cleared_by: str(o, 'cleared_by', what),
    already_cleared: bool(o, 'already_cleared', what),
    note: strOrNull(o, 'note', what),
  };
}

export function parseAlarmEscalateResponse(value: unknown): AlarmEscalateResponse {
  const o = asRecord(value, 'AlarmEscalateResponse');
  const what = 'AlarmEscalateResponse';
  return {
    alarm_id: str(o, 'alarm_id', what),
    // READ from the wire, not pinned to `true`: the contract says `const:
    // true` today, but a parser that hard-codes the answer would mask a
    // server that says otherwise — and the console would show an escalation
    // the server never made.
    escalated: bool(o, 'escalated', what),
    already_escalated: bool(o, 'already_escalated', what),
    escalated_by: str(o, 'escalated_by', what),
    note: strOrNull(o, 'note', what),
  };
}

export function parseAlarmAssignResponse(value: unknown): AlarmAssignResponse {
  const o = asRecord(value, 'AlarmAssignResponse');
  const what = 'AlarmAssignResponse';
  return {
    alarm_id: str(o, 'alarm_id', what),
    assignee: str(o, 'assignee', what),
    previous_assignee: strOrNull(o, 'previous_assignee', what),
    assigned_by: str(o, 'assigned_by', what),
    note: strOrNull(o, 'note', what),
  };
}

/* ------------------------------------------------------------------ */
/* Maintenance windows (ALM-05) — `/ops/v1/maintenance`                */
/* ------------------------------------------------------------------ */

/**
 * One window row, strict: `scope` and `state` are the server's own two-word
 * alphabets (`models.py:558`/`:565`) — a body with a third word is a contract
 * error, not a row the console guesses about. Every transition names a person,
 * so `created_by` is never defaulted.
 */
export function parseMaintenanceWindow(value: unknown): MaintenanceWindow {
  const o = asRecord(value, 'MaintenanceWindow');
  const what = 'MaintenanceWindow';
  return {
    window_id: str(o, 'window_id', what),
    scope: oneOf(o, 'scope', MAINTENANCE_SCOPES, what) as MaintenanceScope,
    target: str(o, 'target', what),
    starts_at: str(o, 'starts_at', what),
    ends_at: str(o, 'ends_at', what),
    note: strOrNull(o, 'note', what),
    state: oneOf(o, 'state', MAINTENANCE_STATES, what) as MaintenanceState,
    created_at: str(o, 'created_at', what),
    created_by: str(o, 'created_by', what),
    cancelled_at: strOrNull(o, 'cancelled_at', what),
    cancelled_by: strOrNull(o, 'cancelled_by', what),
    suppressed: num(o, 'suppressed', what),
  };
}

export function parseMaintenanceList(value: unknown): MaintenanceListResponse {
  const o = asRecord(value, 'MaintenanceListResponse');
  const what = 'MaintenanceListResponse';
  return {
    items: arr(o, 'items', what).map((raw) => parseMaintenanceWindow(raw)),
    total: num(o, 'total', what),
  };
}

export function parseMaintenanceCreateResponse(value: unknown): MaintenanceCreateResponse {
  const o = asRecord(value, 'MaintenanceWindowResponse');
  const what = 'MaintenanceWindowResponse';
  return { item: parseMaintenanceWindow(asRecord(o.item, `${what}.item`)) };
}

/**
 * The cancel answer. `already_cancelled` is the CAS loser's word — the console
 * shows it and the FIRST canceller's name, it does not fold it into a plain
 * «cancelled».
 */
export function parseMaintenanceCancelResponse(value: unknown): MaintenanceCancelResponse {
  const o = asRecord(value, 'MaintenanceCancelResponse');
  const what = 'MaintenanceCancelResponse';
  return {
    window_id: str(o, 'window_id', what),
    state: str(o, 'state', what),
    already_cancelled: bool(o, 'already_cancelled', what),
    cancelled_by: strOrNull(o, 'cancelled_by', what),
    cancelled_at: strOrNull(o, 'cancelled_at', what),
  };
}

/* ---- incidents (N5) — `/ops/v1/incidents` ------------------------------ */

export function parseIncidentTimelineEntry(value: unknown): IncidentTimelineEntry {
  const o = asRecord(value, 'IncidentTimelineEntry');
  const what = 'IncidentTimelineEntry';
  return {
    at: str(o, 'at', what),
    kind: str(o, 'kind', what) as IncidentTimelineEntry['kind'],
    text: strOrNull(o, 'text', what),
    actor: str(o, 'actor', what),
  };
}

export function parseIncident(value: unknown): Incident {
  const o = asRecord(value, 'Incident');
  const what = 'Incident';
  return {
    incident_id: str(o, 'incident_id', what),
    title: str(o, 'title', what),
    severity: str(o, 'severity', what) as Incident['severity'],
    state: str(o, 'state', what) as Incident['state'],
    olt_id: strOrNull(o, 'olt_id', what),
    created_at: str(o, 'created_at', what),
    created_by: str(o, 'created_by', what),
    closed_at: strOrNull(o, 'closed_at', what),
    closed_by: strOrNull(o, 'closed_by', what),
    alarm_ids: strArr(o, 'alarm_ids', what),
    timeline: arr(o, 'timeline', what).map((raw) => parseIncidentTimelineEntry(raw)),
  };
}

export function parseIncidentList(value: unknown): IncidentListResponse {
  const o = asRecord(value, 'IncidentListResponse');
  const what = 'IncidentListResponse';
  return {
    items: arr(o, 'items', what).map((raw) => parseIncident(raw)),
    total: num(o, 'total', what),
  };
}

export function parseIncidentCorrelate(value: unknown): IncidentCorrelateResponse {
  const o = asRecord(value, 'IncidentCorrelateResponse');
  const what = 'IncidentCorrelateResponse';
  return {
    groups: arr(o, 'groups', what).map((raw) => {
      const g = asRecord(raw, `${what}.groups[]`);
      return {
        olt_id: str(g, 'olt_id', what),
        device_count: num(g, 'device_count', what),
        alarm_count: num(g, 'alarm_count', what),
        severity: str(g, 'severity', what) as IncidentGroup['severity'],
        devices: strArr(g, 'devices', what),
        alarm_ids: strArr(g, 'alarm_ids', what),
      };
    }),
    unassociated_devices: num(o, 'unassociated_devices', what),
    scanned: num(o, 'scanned', what),
    truncated: bool(o, 'truncated', what),
    as_of: str(o, 'as_of', what),
  };
}

export function parseIncidentAttachResponse(value: unknown): IncidentAttachResponse {
  const o = asRecord(value, 'IncidentAttachResponse');
  const what = 'IncidentAttachResponse';
  return {
    incident: parseIncident(asRecord(o.incident, `${what}.incident`)),
    attached: strArr(o, 'attached', what),
    already_attached: strArr(o, 'already_attached', what),
  };
}

/* ---- notification channels (N6) — `/ops/v1/notification-channels` ---- */

export function parseNotificationChannel(value: unknown): NotificationChannel {
  const o = asRecord(value, 'NotificationChannel');
  const what = 'NotificationChannel';
  return {
    channel_id: str(o, 'channel_id', what),
    kind: str(o, 'kind', what) as NotificationChannel['kind'],
    name: str(o, 'name', what),
    url: str(o, 'url', what),
    min_severity: str(o, 'min_severity', what) as NotificationChannel['min_severity'],
    enabled: bool(o, 'enabled', what),
    has_secret: bool(o, 'has_secret', what),
    created_by: str(o, 'created_by', what),
    created_at: str(o, 'created_at', what),
    updated_at: str(o, 'updated_at', what),
  };
}

export function parseNotificationChannelList(value: unknown): NotificationChannelListResponse {
  const o = asRecord(value, 'NotificationChannelListResponse');
  const what = 'NotificationChannelListResponse';
  return {
    items: arr(o, 'items', what).map((raw) => parseNotificationChannel(raw)),
    total: num(o, 'total', what),
  };
}

export function parseNotificationChannelDeleted(
  value: unknown,
): NotificationChannelDeletedResponse {
  const o = asRecord(value, 'NotificationChannelDeletedResponse');
  const what = 'NotificationChannelDeletedResponse';
  return {
    deleted: bool(o, 'deleted', what),
    channel_id: str(o, 'channel_id', what),
  };
}

export function parseNotificationDelivery(value: unknown): NotificationDelivery {
  const o = asRecord(value, 'NotificationDelivery');
  const what = 'NotificationDelivery';
  return {
    delivery_id: str(o, 'delivery_id', what),
    channel_id: str(o, 'channel_id', what),
    channel_name: str(o, 'channel_name', what),
    kind: str(o, 'kind', what) as NotificationDelivery['kind'],
    alarm_id: strOrNull(o, 'alarm_id', what),
    alarm_type: str(o, 'alarm_type', what),
    severity: str(o, 'severity', what) as NotificationDelivery['severity'],
    acs_device_id: str(o, 'acs_device_id', what),
    occurred_at: str(o, 'occurred_at', what),
    status: str(o, 'status', what) as NotificationDelivery['status'],
    attempts: num(o, 'attempts', what),
    last_error: strOrNull(o, 'last_error', what),
    next_attempt_at: strOrNull(o, 'next_attempt_at', what),
    created_at: str(o, 'created_at', what),
    finished_at: strOrNull(o, 'finished_at', what),
  };
}

export function parseNotificationDeliveryList(value: unknown): NotificationDeliveryListResponse {
  const o = asRecord(value, 'NotificationDeliveryListResponse');
  const what = 'NotificationDeliveryListResponse';
  return {
    items: arr(o, 'items', what).map((raw) => parseNotificationDelivery(raw)),
    total: num(o, 'total', what),
  };
}

/* ------------------------------------------------------------------ */
/* OLT views + optical — `/ops/v1/olts…`, `…/optical`                  */
/* ------------------------------------------------------------------ */

/** `OltInventoryItem` — `note` is what makes a 0 count honest. */
export function parseOltItem(value: unknown): OltInventoryItem {
  const o = asRecord(value, 'OltInventoryItem');
  const what = 'OltInventoryItem';
  return {
    olt_id: str(o, 'olt_id', what),
    brand: str(o, 'brand', what),
    host: str(o, 'host', what),
    enabled: bool(o, 'enabled', what),
    onu_count: num(o, 'onu_count', what),
    note: strOrNull(o, 'note', what),
  };
}

export function parseOltList(value: unknown): OltListResponse {
  const o = asRecord(value, 'OltListResponse');
  const what = 'OltListResponse';
  return {
    items: arr(o, 'items', what).map((raw) => parseOltItem(raw)),
    total: num(o, 'total', what),
  };
}

/**
 * One ONU row. The contract types these as free objects (adapter passthrough),
 * so only `sn` is required — it is the row key and the optical lookup — and the
 * other keys are validated only for the type they must have when present.
 * Unknown keys are kept verbatim instead of being dropped.
 */
export function parseOnuRow(value: unknown): OnuTableRow {
  const row = asRecord(value, 'OnuTableRow');
  const what = 'OnuTableRow';
  for (const key of ['olt_id', 'pon_port', 'status', 'auth_state']) {
    const entry = row[key];
    if (entry !== undefined && typeof entry !== 'string') {
      fail(what, `field "${key}" must be a string when present`);
    }
  }
  for (const key of ['profile', 'line_profile', 'service_profile']) {
    const entry = row[key];
    if (entry !== undefined && entry !== null && typeof entry !== 'string') {
      fail(what, `field "${key}" must be a string or null when present`);
    }
  }
  const vlan = row.vlan;
  if (vlan !== undefined && vlan !== null && typeof vlan !== 'string' && typeof vlan !== 'number') {
    fail(what, 'field "vlan" must be a string, a number or null when present');
  }
  const tr069 = row.tr069;
  if (tr069 !== undefined && tr069 !== null && typeof tr069 !== 'object') {
    fail(what, 'field "tr069" must be an object or null when present');
  }
  return { ...row, sn: str(row, 'sn', what) };
}

export function parseOnuTable(value: unknown): OnuTableResponse {
  const o = asRecord(value, 'OnuTableResponse');
  const what = 'OnuTableResponse';
  return {
    olt_id: str(o, 'olt_id', what),
    items: arr(o, 'items', what).map((raw) => parseOnuRow(raw)),
    total: num(o, 'total', what),
  };
}

/**
 * V7 (G-PRC-08) — one measured PON port. `capacity` is the DECLARED number the operator
 * wrote, `remaining` may be NEGATIVE (a port past the declaration is the signal, and a
 * parser that clamped it would delete that signal on its way to the screen), and
 * `at_capacity` is the inclusive boundary.
 */
function parsePonCapacityRow(value: unknown): PonCapacityRow {
  const o = asRecord(value, 'PonCapacityRow');
  const what = 'PonCapacityRow';
  return {
    pon_port: str(o, 'pon_port', what),
    onu_count: num(o, 'onu_count', what),
    capacity: num(o, 'capacity', what),
    utilization_pct: num(o, 'utilization_pct', what),
    remaining: num(o, 'remaining', what),
    at_capacity: bool(o, 'at_capacity', what),
  };
}

/**
 * `PonCapacityResponse` — the measured table with the declaration named.
 * `ports` is nullable (null = nobody read the raw table) but NOT optional:
 * an absent `ports` would be a third state the door never answers.
 */
export function parsePonCapacity(value: unknown): PonCapacityResponse {
  const o = asRecord(value, 'PonCapacityResponse');
  const what = 'PonCapacityResponse';
  const ports = o.ports;
  if (ports !== null && !Array.isArray(ports)) {
    fail(what, 'field "ports" must be an array or null');
  }
  const source = str(o, 'capacity_source', what);
  if (!source.startsWith('settings:')) {
    fail(what, 'field "capacity_source" must name the settings key the capacity came from');
  }
  return {
    olt_id: str(o, 'olt_id', what),
    declared_capacity: num(o, 'declared_capacity', what),
    capacity_source: source,
    ports: ports === null ? null : (ports as unknown[]).map((raw) => parsePonCapacityRow(raw)),
    ports_counted: num(o, 'ports_counted', what),
    total_onus: num(o, 'total_onus', what),
    ports_at_capacity: num(o, 'ports_at_capacity', what),
  };
}

export function parseOnuAuthorizeResponse(value: unknown): OnuAuthorizeResponse {
  const o = asRecord(value, 'OnuAuthorizeResponse');
  const what = 'OnuAuthorizeResponse';
  return {
    olt_id: str(o, 'olt_id', what),
    sn: str(o, 'sn', what),
    pon_port: str(o, 'pon_port', what),
    admin_state: str(o, 'admin_state', what),
    authorized: bool(o, 'authorized', what),
  };
}

/**
 * `OpticalRead` — one instantaneous sample. `rx_dbm`/`tx_dbm` are nullable
 * because the adapter answers null when the SNMP read came back empty, which is
 * a real reading, not a missing field.
 */
export function parseOpticalRead(value: unknown): OpticalRead {
  const o = asRecord(value, 'OpticalRead');
  const what = 'OpticalRead';
  return {
    rx_dbm: numOrNull(o, 'rx_dbm', what),
    tx_dbm: numOrNull(o, 'tx_dbm', what),
    measured_at: str(o, 'measured_at', what),
    olt_id: str(o, 'olt_id', what),
    sn: str(o, 'sn', what),
  };
}

/**
 * One bucket of `OpticalHistory`. `rx_dbm` is NOT nullable here: a bucket exists only
 * because rows were stored in it, and the mean of those rows is a number. `samples` is
 * the count that mean rests on — a bucket claiming zero samples is a point about nothing.
 */
function parseOpticalHistoryPoint(value: unknown): OpticalHistoryPoint {
  const o = asRecord(value, 'OpticalHistoryPoint');
  const what = 'OpticalHistoryPoint';
  const samples = num(o, 'samples', what);
  if (!Number.isInteger(samples) || samples < 1) {
    fail(
      what,
      `field "samples" must be an integer of at least 1 — an empty bucket is absent, got ${samples}`,
    );
  }
  return {
    at: str(o, 'at', what),
    rx_dbm: num(o, 'rx_dbm', what),
    tx_dbm: numOrNull(o, 'tx_dbm', what),
    samples,
  };
}

/**
 * `OpticalHistory` — the stored series of one device over one window (V3). The window is
 * the published enum and `bucket_seconds` is the width that window reports at; a point list
 * is taken as it comes, because an absent bucket IS the answer for that slice of time.
 */
export function parseOpticalHistory(value: unknown): OpticalHistory {
  const o = asRecord(value, 'OpticalHistory');
  const what = 'OpticalHistory';
  const window = str(o, 'window', what);
  if (!(OPTICAL_WINDOWS as readonly string[]).includes(window)) {
    fail(what, `field "window" must be one of ${OPTICAL_WINDOWS.join(', ')}, got ${window}`);
  }
  return {
    acs_device_id: str(o, 'acs_device_id', what),
    olt_id: strOrNull(o, 'olt_id', what),
    window: window as OpticalWindow,
    window_start: str(o, 'window_start', what),
    window_end: str(o, 'window_end', what),
    bucket_seconds: num(o, 'bucket_seconds', what),
    points: arr(o, 'points', what).map((entry) => parseOpticalHistoryPoint(entry)),
    samples: num(o, 'samples', what),
    last_sampled_at: strOrNull(o, 'last_sampled_at', what),
    note: str(o, 'note', what),
  };
}

/**
 * One row of the fleet's worst list (V3b). `rx_min_dbm` is NOT nullable — a device is ranked
 * because it has a darkest reading — and `samples` must be at least 1 for the same reason a
 * history bucket must: a row about zero measurements is a row about nothing.
 */
function parseOpticalWorstDevice(value: unknown): OpticalWorstDevice {
  const o = asRecord(value, 'OpticalWorstDevice');
  const what = 'OpticalWorstDevice';
  const samples = num(o, 'samples', what);
  if (!Number.isInteger(samples) || samples < 1) {
    fail(what, `field "samples" must be an integer of at least 1, got ${samples}`);
  }
  return {
    acs_device_id: str(o, 'acs_device_id', what),
    rx_min_dbm: num(o, 'rx_min_dbm', what),
    rx_avg_dbm: numOrNull(o, 'rx_avg_dbm', what),
    samples,
    last_sampled_at: strOrNull(o, 'last_sampled_at', what),
  };
}

/** One occupied bin of the RX histogram. `devices` is the bar's height, so a zero bar is a lie. */
function parseOpticalRxBin(value: unknown): OpticalRxBin {
  const o = asRecord(value, 'OpticalRxBin');
  const what = 'OpticalRxBin';
  const devices = num(o, 'devices', what);
  if (!Number.isInteger(devices) || devices < 1) {
    fail(
      what,
      `field "devices" must be an integer of at least 1 — an empty bin is absent, got ${devices}`,
    );
  }
  return { bin_dbm: num(o, 'bin_dbm', what), devices };
}

/**
 * `OpticalWorst` — the fleet's darkest ONUs and the RX distribution (V3b). The window is the
 * published enum, and `worst`/`histogram` are taken as they come: an unmeasured fleet really is
 * two empty lists, and `devices` says so without inventing a bar.
 */
export function parseOpticalWorst(value: unknown): OpticalWorst {
  const o = asRecord(value, 'OpticalWorst');
  const what = 'OpticalWorst';
  const window = str(o, 'window', what);
  if (!(OPTICAL_WINDOWS as readonly string[]).includes(window)) {
    fail(what, `field "window" must be one of ${OPTICAL_WINDOWS.join(', ')}, got ${window}`);
  }
  return {
    window: window as OpticalWindow,
    window_start: str(o, 'window_start', what),
    window_end: str(o, 'window_end', what),
    limit: num(o, 'limit', what),
    devices: num(o, 'devices', what),
    samples: num(o, 'samples', what),
    bin_dbm: num(o, 'bin_dbm', what),
    worst: arr(o, 'worst', what).map((entry) => parseOpticalWorstDevice(entry)),
    histogram: arr(o, 'histogram', what).map((entry) => parseOpticalRxBin(entry)),
    note: str(o, 'note', what),
  };
}

/**
 * One row of the fleet's decline list (V3c). Three refusals, each one a sentence the door would
 * otherwise be able to say wrongly:
 *
 *  - `rate_dbm_per_day` must be **negative** — a published row that rose or sat flat is not a
 *    decline, and the list is called `declining`;
 *  - both halves must clear the published guard, because the door's whole claim is that it refused
 *    to judge thinner data than that;
 *  - `days_to_warn` must be null when `already_below_warn` — an ONU past the line has no future
 *    countdown to report, and a positive number beside that flag is the contradiction the panel
 *    would render as a warning about a device that is already failing.
 */
function parseOpticalDegradingDevice(value: unknown): OpticalDegradingDevice {
  const o = asRecord(value, 'OpticalDegradingDevice');
  const what = 'OpticalDegradingDevice';
  const rate = num(o, 'rate_dbm_per_day', what);
  if (!(rate < 0)) {
    fail(what, `field "rate_dbm_per_day" must be negative in a decline list, got ${rate}`);
  }
  const samplesFirst = num(o, 'samples_first', what);
  if (!(samplesFirst >= DEGRADING_MIN_SAMPLES_PER_HALF)) {
    fail(
      what,
      `field "samples_first" must be at least ${DEGRADING_MIN_SAMPLES_PER_HALF}, got ${samplesFirst}`,
    );
  }
  const samplesSecond = num(o, 'samples_second', what);
  if (!(samplesSecond >= DEGRADING_MIN_SAMPLES_PER_HALF)) {
    fail(
      what,
      `field "samples_second" must be at least ${DEGRADING_MIN_SAMPLES_PER_HALF}, got ${samplesSecond}`,
    );
  }
  const already = bool(o, 'already_below_warn', what);
  const days = numOrNull(o, 'days_to_warn', what);
  if (already && days !== null) {
    fail(what, `field "days_to_warn" must be null when already_below_warn is true, got ${days}`);
  }
  return {
    acs_device_id: str(o, 'acs_device_id', what),
    rx_avg_first: num(o, 'rx_avg_first', what),
    rx_avg_second: num(o, 'rx_avg_second', what),
    rx_min_second: numOrNull(o, 'rx_min_second', what),
    samples_first: samplesFirst,
    samples_second: samplesSecond,
    rate_dbm_per_day: rate,
    days_to_warn: days,
    within_horizon: bool(o, 'within_horizon', what),
    already_below_warn: already,
    last_sampled_at: strOrNull(o, 'last_sampled_at', what),
  };
}

/**
 * `OpticalDegrading` — the fleet's decline rates (V3c, G-TLM-03).
 *
 * The window is the door's OWN two-value enum, not the four windows the other optical doors answer:
 * a dBm/day rate from two hours of data is a guess wearing a unit, and the parser is where a console
 * finds out the server refused to make one. The guard and the horizon are compared against the
 * published constants rather than trusted, because the panel prints both numbers as the reason a row
 * exists or does not.
 */
export function parseOpticalDegrading(value: unknown): OpticalDegrading {
  const o = asRecord(value, 'OpticalDegrading');
  const what = 'OpticalDegrading';
  const window = str(o, 'window', what);
  if (!(DEGRADING_WINDOWS as readonly string[]).includes(window)) {
    fail(what, `field "window" must be one of ${DEGRADING_WINDOWS.join(', ')}, got ${window}`);
  }
  const horizon = num(o, 'horizon_days', what);
  if (horizon !== DEGRADING_HORIZON_DAYS) {
    fail(what, `field "horizon_days" must be ${DEGRADING_HORIZON_DAYS}, got ${horizon}`);
  }
  const guard = num(o, 'min_samples_per_half', what);
  if (guard !== DEGRADING_MIN_SAMPLES_PER_HALF) {
    fail(
      what,
      `field "min_samples_per_half" must be ${DEGRADING_MIN_SAMPLES_PER_HALF}, got ${guard}`,
    );
  }
  return {
    window: window as OpticalDegradingWindow,
    window_start: str(o, 'window_start', what),
    window_end: str(o, 'window_end', what),
    half_seconds: num(o, 'half_seconds', what),
    limit: num(o, 'limit', what),
    devices: num(o, 'devices', what),
    samples: num(o, 'samples', what),
    insufficient: num(o, 'insufficient', what),
    warn_rx_dbm: num(o, 'warn_rx_dbm', what),
    critical_rx_dbm: num(o, 'critical_rx_dbm', what),
    horizon_days: horizon,
    min_samples_per_half: guard,
    declining: arr(o, 'declining', what).map((entry) => parseOpticalDegradingDevice(entry)),
    note: str(o, 'note', what),
  };
}

/* ------------------------------------------------------------------ */
/* Firmware registry + campaigns — `/ops/v1/firmware…`, `/campaigns…`   */
/* ------------------------------------------------------------------ */
function numArr(obj: Record<string, unknown>, key: string, what: string): number[] {
  return arr(obj, key, what).map((entry, index) => {
    if (typeof entry !== 'number' || !Number.isFinite(entry)) {
      fail(what, `field "${key}[${index}]" must be a number`);
    }
    return entry;
  });
}

function strArr(obj: Record<string, unknown>, key: string, what: string): string[] {
  return arr(obj, key, what).map((entry, index) => {
    if (typeof entry !== 'string') {
      fail(what, `field "${key}[${index}]" must be a string`);
    }
    return entry;
  });
}

/** `FirmwareFile` — metadata only; the image bytes are never on this surface. */
export function parseFirmwareFile(value: unknown): FirmwareFile {
  const o = asRecord(value, 'FirmwareFile');
  const what = 'FirmwareFile';
  return {
    file_id: str(o, 'file_id', what),
    brand: str(o, 'brand', what),
    model: str(o, 'model', what),
    version: str(o, 'version', what),
    checksum_sha256: str(o, 'checksum_sha256', what),
    size_bytes: num(o, 'size_bytes', what),
    status: str(o, 'status', what),
    genieacs_file: str(o, 'genieacs_file', what),
    created_at: strOrNull(o, 'created_at', what),
  };
}

export function parseFirmwareList(value: unknown): FirmwareListResponse {
  const o = asRecord(value, 'FirmwareListResponse');
  const what = 'FirmwareListResponse';
  return {
    items: arr(o, 'items', what).map((raw) => parseFirmwareFile(raw)),
    next_cursor: cursor(o, what),
  };
}

/** `FirmwareUploadAccepted` — either registration door answers this. */
export function parseFirmwareUploadAccepted(value: unknown): FirmwareUploadAccepted {
  const o = asRecord(value, 'FirmwareUploadAccepted');
  const what = 'FirmwareUploadAccepted';
  return {
    file_id: str(o, 'file_id', what),
    brand: str(o, 'brand', what),
    model: str(o, 'model', what),
    version: str(o, 'version', what),
    status: str(o, 'status', what),
  };
}

/** `DeletedResponse.deleted` is `{ const: true }`, so the field is not re-read. */
export function parseDeletedResponse(value: unknown): DeletedResponse {
  asRecord(value, 'DeletedResponse');
  return { deleted: true };
}

/**
 * `CampaignFilter` — the operator's inputs, echoed back. Every field is optional on the
 * wire and null-tolerant, so only the types are pinned; an unexpected shape here would
 * silently change the population a page is describing.
 */
function parseCampaignFilter(value: unknown): CampaignFilter {
  const o = asRecord(value, 'CampaignFilter');
  const what = 'CampaignFilter';
  const out: CampaignFilter = {};
  if (o.tags !== undefined) out.tags = strArr(o, 'tags', what);
  for (const key of ['manufacturer', 'model', 'from_version'] as const) {
    if (o[key] !== undefined) out[key] = strOrNull(o, key, what);
  }
  return out;
}

/** `CampaignProgress` — read-time counts over the campaign's download jobs. */
export function parseCampaignProgress(value: unknown): CampaignProgress {
  const o = asRecord(value, 'CampaignProgress');
  const what = 'CampaignProgress';
  return {
    queued: num(o, 'queued', what),
    running: num(o, 'running', what),
    completed: num(o, 'completed', what),
    failed: num(o, 'failed', what),
    total: num(o, 'total', what),
  };
}

/** `CampaignWindow` — `required: [status, open, starts_at, ends_at]`, and `passed` is a status. */
export function parseCampaignWindow(value: unknown): CampaignWindow {
  const o = asRecord(value, 'CampaignWindow');
  const what = 'CampaignWindow';
  return {
    status: oneOf(o, 'status', CAMPAIGN_WINDOW_STATUSES, what) as CampaignWindow['status'],
    open: bool(o, 'open', what),
    starts_at: str(o, 'starts_at', what),
    ends_at: str(o, 'ends_at', what),
  };
}

/** `CampaignWave` — a cumulative share, so `pct` is the only field. */
function parseCampaignWave(value: unknown): CampaignWave {
  const o = asRecord(value, 'CampaignWave');
  return { pct: num(o, 'pct', 'CampaignWave') };
}

/**
 * `CampaignNextAction` — the decision the campaign worker is acting on.
 *
 * `action` is pinned to the contract's six words: a seventh verdict the console cannot
 * render must become a visible `contract` error rather than an empty panel, because this
 * is the field that answers «why is my wave not moving?».
 */
export function parseCampaignNextAction(value: unknown): CampaignNextAction {
  const o = asRecord(value, 'CampaignNextAction');
  const what = 'CampaignNextAction';
  const window = o.window;
  const openJobs = o.open_jobs;
  const devices = o.devices;
  return {
    campaign_id: str(o, 'campaign_id', what),
    wave: num(o, 'wave', what),
    kind: oneOf(o, 'kind', CAMPAIGN_KINDS, what) as CampaignNextAction['kind'],
    status: str(o, 'status', what),
    window: window === undefined || window === null ? null : parseCampaignWindow(window),
    action: oneOf(o, 'action', CAMPAIGN_ACTIONS, what) as CampaignNextAction['action'],
    reason: str(o, 'reason', what),
    ...(typeof openJobs === 'number' ? { open_jobs: openJobs } : {}),
    ...(devices === undefined ? {} : { devices: strArr(o, 'devices', what) }),
  };
}

/** `CampaignWaveInfo` — `devices` counts what the request dispatched, not the wave. */
function parseCampaignWaveInfo(value: unknown): CampaignWaveInfo {
  const o = asRecord(value, 'CampaignWaveInfo');
  const what = 'CampaignWaveInfo';
  return { index: num(o, 'index', what), devices: num(o, 'devices', what) };
}

/** The list view's shared field set (`CampaignListResponse.items` re-declares the detail's). */
function campaignCoreFields(o: Record<string, unknown>, what: string) {
  return {
    campaign_id: str(o, 'campaign_id', what),
    status: oneOf(o, 'status', CAMPAIGN_STATUSES, what) as Campaign['status'],
    file_id: str(o, 'file_id', what),
    brand: str(o, 'brand', what),
    model: str(o, 'model', what),
    version: str(o, 'version', what),
    current_wave: num(o, 'current_wave', what),
    per_wave: numArr(o, 'per_wave', what),
    population: num(o, 'population', what),
    queued_devices: num(o, 'queued_devices', what),
    remaining_devices: num(o, 'remaining_devices', what),
    progress: parseCampaignProgress(o.progress),
    started_at: strOrNull(o, 'started_at', what),
    created_at: strOrNull(o, 'created_at', what),
    profile_version: numOrNull(o, 'profile_version', what),
  };
}

export function parseCampaignListItem(value: unknown): CampaignListItem {
  const o = asRecord(value, 'CampaignListItem');
  return campaignCoreFields(o, 'CampaignListItem');
}

export function parseCampaignList(value: unknown): CampaignListResponse {
  const o = asRecord(value, 'CampaignListResponse');
  const what = 'CampaignListResponse';
  return {
    items: arr(o, 'items', what).map((raw) => parseCampaignListItem(raw)),
    next_cursor: cursor(o, what),
  };
}

/** `Campaign` — the detail. The internal job map is not on the wire and is not invented here. */
export function parseCampaign(value: unknown): Campaign {
  const o = asRecord(value, 'Campaign');
  const what = 'Campaign';
  return {
    ...campaignCoreFields(o, what),
    filter: parseCampaignFilter(o.filter),
    waves: arr(o, 'waves', what).map((raw) => parseCampaignWave(raw)),
    targets: strArr(o, 'targets', what),
    queued: strArr(o, 'queued', what),
    halt_on_fail_pct: num(o, 'halt_on_fail_pct', what),
    maintenance_window: strOrNull(o, 'maintenance_window', what),
    next_action: parseCampaignNextAction(o.next_action),
    dispatch_kind: oneOf(o, 'dispatch_kind', CAMPAIGN_KINDS, what) as Campaign['dispatch_kind'],
    dispatch_file_id: str(o, 'dispatch_file_id', what),
    updated_at: strOrNull(o, 'updated_at', what),
    // W8/C2: the record's own continuous intent. A record that predates W8 carries none of the
    // three — absent `continuous` IS false (that campaign was not continuous), absent
    // `ladder_waves` is the per_wave length it was created with (the create-time invariant, not a
    // guess), and an absent/null batch is no batch.
    continuous: typeof o['continuous'] === 'boolean' ? o['continuous'] : false,
    ladder_waves:
      typeof o['ladder_waves'] === 'number'
        ? o['ladder_waves']
        : ((o['per_wave'] as unknown[] | undefined)?.length ?? 0),
    continuous_batch:
      o['continuous_batch'] === undefined || o['continuous_batch'] === null
        ? null
        : strArr(o, 'continuous_batch', what),
  };
}

/** `CampaignScheduled` — E4's create answer: a schedule, and nothing dispatched. */
export function parseCampaignScheduled(value: unknown): CampaignScheduled {
  const o = asRecord(value, 'CampaignScheduled');
  const what = 'CampaignScheduled';
  return {
    campaign_id: str(o, 'campaign_id', what),
    status: oneOf(o, 'status', ['pending', 'completed'], what) as CampaignScheduled['status'],
    wave: parseCampaignWaveInfo(o.wave),
    queued_devices: num(o, 'queued_devices', what),
    next_action: parseCampaignNextAction(o.next_action),
  };
}

/** `CampaignAccepted` — what `:resume` answers, including the recorded window override. */
export function parseCampaignAccepted(value: unknown): CampaignAccepted {
  const o = asRecord(value, 'CampaignAccepted');
  const what = 'CampaignAccepted';
  const outside = o.dispatched_outside_window;
  const window = o.window;
  return {
    campaign_id: str(o, 'campaign_id', what),
    status: oneOf(o, 'status', CAMPAIGN_STATUSES, what) as CampaignAccepted['status'],
    wave: parseCampaignWaveInfo(o.wave),
    ...(typeof outside === 'boolean' ? { dispatched_outside_window: outside } : {}),
    ...(window === undefined || window === null ? {} : { window: parseCampaignWindow(window) }),
  };
}

export function parseCampaignHalted(value: unknown): CampaignHalted {
  const o = asRecord(value, 'CampaignHalted');
  const what = 'CampaignHalted';
  return {
    campaign_id: str(o, 'campaign_id', what),
    status: oneOf(o, 'status', ['halted'], what) as 'halted',
  };
}

export function parseCampaignRolledBack(value: unknown): CampaignRolledBack {
  const o = asRecord(value, 'CampaignRolledBack');
  const what = 'CampaignRolledBack';
  return {
    campaign_id: str(o, 'campaign_id', what),
    status: oneOf(o, 'status', CAMPAIGN_STATUSES, what) as CampaignRolledBack['status'],
    rollback_file_id: str(o, 'rollback_file_id', what),
    wave: parseCampaignWaveInfo(o.wave),
  };
}

/* ------------------------------------------------------------------ */
/* audit ledger — `GET /ops/v1/audit` (R7 / U-16-d)                    */
/* ------------------------------------------------------------------ */

/**
 * One stored entry, VERBATIM.
 *
 * The door publishes the record its operation wrote and declares no summary fields for kinds it does
 * not own (`routers/audit.py`: «VERBATIM, NOT SUMMARISED … a router that starts recording a new key
 * shows up here without a contract change»), so this function must NOT narrow the entry: it keeps
 * every key and validates only the three the store and every filter guarantee — `id` (the row key the
 * page keys on), `created_at` (the ledger's own order) and `kind` (the only way a filter can find it).
 * The five actor shapes of پیوست ب are deliberately not validated here: deciding what a row means is
 * `lib/auditActor.ts`'s job, and a parser that refused an unknown shape would turn a historical row
 * into a dropped row — the silent-loss failure §۸ (۷) refuses.
 */
export function parseAuditEntry(value: unknown): AuditEntry {
  const o = asRecord(value, 'AuditEntry');
  const what = 'AuditEntry';
  for (const key of ['id', 'created_at', 'kind']) {
    if (typeof o[key] !== 'string') fail(what, `field "${key}" must be a string`);
  }
  return { ...o };
}

/**
 * `AuditListResponse` — `total` is the count for THIS filter over the whole ledger (not the page),
 * and `note` is required: the honesty clause is what separates «no rows matched» from «nothing
 * happened», and a payload that lost it is a contract break, not a cosmetic omission.
 */
export function parseAuditList(value: unknown): AuditListResponse {
  const o = asRecord(value, 'AuditListResponse');
  const what = 'AuditListResponse';
  return {
    entries: arr(o, 'entries', what).map((raw) => parseAuditEntry(raw)),
    total: num(o, 'total', what),
    next_cursor: cursor(o, what),
    note: str(o, 'note', what),
  };
}

export function parseProblem(value: unknown): Problem {
  const o = asRecord(value, 'Problem');
  const what = 'Problem';
  const code = str(o, 'code', what);
  if (!PROBLEM_CODE_SET.has(code)) {
    fail(what, `unknown problem code "${code}" (not in the contract enum)`);
  }
  const instance = strOrNull(o, 'instance', what);
  // RFC 9457 extensions travel with the problem and the profile doors publish them as the
  // actionable half of the answer: 409 `profile-stale-version` carries `current_version` (the
  // store's own read-back) and 422 `profile-invalid` / 409 `profile-release-blocked` carry the
  // whole report in `problems`. Dropping them here would make the console paraphrase the server
  // instead of quoting it (D6), so every key outside the five core fields survives verbatim.
  const { type, title, status, detail, code: _code, instance: _instance, ...rest } = o;
  void type;
  void title;
  void status;
  void detail;
  return {
    type: str(o, 'type', what),
    title: str(o, 'title', what),
    status: num(o, 'status', what),
    detail: str(o, 'detail', what),
    code: code as ProblemCode,
    ...(instance === null ? {} : { instance }),
    ...rest,
  };
}

/* ------------------------------------------------------------------ */
/* human identity (R1 / U-08-f)                                          */
/* ------------------------------------------------------------------ */

/**
 * `capabilities` is `additionalProperties: {enum: [read, edit]}` in the contract, so a value
 * outside that pair is a drift in the vocabulary and not a level the UI can render.
 */
function capabilityMap(obj: Record<string, unknown>, what: string): Record<string, CapabilityWire> {
  const raw = obj.capabilities;
  if (raw === undefined || raw === null) return {};
  const record = asRecord(raw, what);
  const out: Record<string, CapabilityWire> = {};
  for (const [capability, level] of Object.entries(record)) {
    if (level !== 'read' && level !== 'edit') {
      fail(
        what,
        `capabilities["${capability}"] must be read or edit, got ${JSON.stringify(level)}`,
      );
    }
    out[capability] = level;
  }
  return out;
}

export function parseSessionGranted(value: unknown): SessionGranted {
  const o = asRecord(value, 'SessionGranted');
  const what = 'SessionGranted';
  const mfa = o['mfa_required'];
  const challenge = o['challenge'];
  const challengeExpires = o['challenge_expires_at'];
  return {
    token: str(o, 'token', what),
    role: str(o, 'role', what),
    expires_at: str(o, 'expires_at', what),
    sid: str(o, 'sid', what),
    capabilities: capabilityMap(o, what),
    // The R8 fields are copied only when the answer carried them, so a one-step login still
    // parses to exactly the five-field grant it always did: «this account has no second factor»
    // and «the second factor was left blank» must not be the same object.
    ...(mfa === true ? { mfa_required: true as const } : {}),
    ...(typeof challenge === 'string' ? { challenge } : {}),
    ...(typeof challengeExpires === 'string' ? { challenge_expires_at: challengeExpires } : {}),
  };
}

export function parseSessionRevoked(value: unknown): SessionRevoked {
  const o = asRecord(value, 'SessionRevoked');
  const what = 'SessionRevoked';
  return {
    sid: str(o, 'sid', what),
    revoked_at: str(o, 'revoked_at', what),
    detail: str(o, 'detail', what),
  };
}

/**
 * The mirror's only source. `kind` is a closed enum in the document, so an unknown one is a
 * contract break: a console that guessed a kind for it would be rendering an identity nobody
 * published.
 */
export function parseEffectivePermissions(value: unknown): EffectivePermissions {
  const o = asRecord(value, 'EffectivePermissions');
  const what = 'EffectivePermissions';
  return {
    kind: oneOf(o, 'kind', ['human', 'machine'], what) as EffectivePermissions['kind'],
    subject: str(o, 'subject', what),
    role: strOrNull(o, 'role', what),
    capabilities: capabilityMap(o, what),
    roles: strArr(o, 'roles', what),
  };
}

/* ------------------------------------------------------------------ */
/* signing keys — `GET /ops/v1/keys` (U-08-b door, U-16-e1 page)        */
/* ------------------------------------------------------------------ */

/**
 * JWK field names that carry key material, from the published document's own vocabulary.
 *
 * `docs/plans/2026-10-17-u16-console-workflows-and-audit-actors.md` lists «هر کلید خصوصی در پاسخ» as
 * the failure this page must not survive, and the plan's global rule is that no private key reaches
 * the browser at all (AGENTS invariant ۴). The list is checked against the server's own measurement —
 * `tests/test_acs_u16e_keys_door.py` records which names the published JWKS actually uses in
 * `fixtures/keys-measured-shapes.json` — so a name the door starts publishing that is not refused here
 * is a red test, not a rendered key.
 */
const KEY_MATERIAL_FIELDS: readonly string[] = ['d', 'x', 'kty', 'crv', 'use'];

/** The row fields the door publishes, in the order the artefact's `rowKeys` describe. */
const KEY_ROW_FIELDS: readonly string[] = ['kid', 'alg', 'created_at', 'not_after', 'active'];

/**
 * One signing-key row: the five metadata fields, and nothing that could be a key.
 *
 * `SigningKeyMetadata` is a closed Pydantic model, so a sixth field can only arrive with a contract
 * change — which is why refusing the material names here is cheap and why an extra unknown field is
 * an error rather than something to spread into the row.
 */
export function parseSigningKeyMetadata(value: unknown): SigningKeyMetadata {
  const o = asRecord(value, 'SigningKeyMetadata');
  const what = 'SigningKeyMetadata';
  const material = KEY_MATERIAL_FIELDS.filter((field) => field in o);
  if (material.length > 0) {
    fail(what, `refusing a response that carries key material (${material.join(', ')})`);
  }
  const extra = Object.keys(o).filter((field) => !KEY_ROW_FIELDS.includes(field));
  if (extra.length > 0) {
    fail(what, `field(s) the contract does not publish: ${extra.join(', ')}`);
  }
  return {
    kid: str(o, 'kid', what),
    alg: str(o, 'alg', what),
    created_at: strOrNull(o, 'created_at', what),
    not_after: strOrNull(o, 'not_after', what),
    active: bool(o, 'active', what),
  };
}

/**
 * `SigningKeyList` — `{ keys: [...] }` and nothing else.
 *
 * No cursor, no total, no note: those belong to the paginated and the ledger doors, and this one
 * publishes neither (`acs_service/models.py::SigningKeyList`). A parser that tolerated a `next_cursor`
 * here would let a page grow pagination the door does not have.
 */
export function parseSigningKeyList(value: unknown): SigningKeyListResponse {
  const o = asRecord(value, 'SigningKeyListResponse');
  const what = 'SigningKeyListResponse';
  const extra = Object.keys(o).filter((field) => field !== 'keys');
  if (extra.length > 0) {
    fail(what, `field(s) the contract does not publish: ${extra.join(', ')}`);
  }
  return { keys: arr(o, 'keys', what).map((raw) => parseSigningKeyMetadata(raw)) };
}

/* ------------------------------------------------------------------ */
/* operator profile — the six doors of `routers/profile.py` (U-16-e2)  */
/* ------------------------------------------------------------------ */

/** The candidate document, kept VERBATIM: the profile is strict and the console mirrors it, never re-models it. */
function profileDocument(value: unknown, what: string): ProfileDocument {
  return { ...asRecord(value, what) };
}

/** The nested `{kind, subject}` actor pair — shape (ج) of پیوست ب, published typed by `/versions` (D2). */
function profileActor(value: unknown, what: string): Record<string, string> {
  const o = asRecord(value, what);
  const out: Record<string, string> = {};
  for (const [key, raw] of Object.entries(o)) {
    if (typeof raw !== 'string') fail(what, `actor.${key} must be a string`);
    out[key] = raw as string;
  }
  return out;
}

/** `ProfileProblemReport` — three strings, and a fourth key would already be a second vocabulary. */
export function parseProfileProblemReport(value: unknown): ProfileProblemReport {
  const o = asRecord(value, 'ProfileProblemReport');
  const what = 'ProfileProblemReport';
  return {
    path: str(o, 'path', what),
    rule: str(o, 'rule', what),
    detail: str(o, 'detail', what),
  };
}

function parseProfileDiffEntry(value: unknown): ProfileDiffEntry {
  const o = asRecord(value, 'ProfileDiffEntry');
  const what = 'ProfileDiffEntry';
  if (typeof o.path !== 'string') fail(what, 'field "path" must be a string');
  return {
    path: o.path as string,
    // `before`/`after` are three-state answers («absent», «null», «a value») — no coercion, no default.
    ...(Object.prototype.hasOwnProperty.call(o, 'before') ? { before: o.before } : {}),
    ...(Object.prototype.hasOwnProperty.call(o, 'after') ? { after: o.after } : {}),
  };
}

/**
 * `ProfileValidationReport` — the report is the product, so the parser must not drop a single
 * problem: one lost line turns «همهٔ خطاها در یک گزارش قابل رفع» back into the ten-round-trip edit
 * the door exists to prevent. `release_blocked` and `valid` are required booleans (both are
 * non-defaulted in the model precisely so a door cannot answer them by forgetting to compute them).
 */
export function parseProfileValidationReport(value: unknown): ProfileValidationReport {
  const o = asRecord(value, 'ProfileValidationReport');
  const what = 'ProfileValidationReport';
  const problems = arr(o, 'problems', what);
  const diff = arr(o, 'diff', what);
  const base = o.base_version;
  if (base !== null && base !== undefined && typeof base !== 'number') {
    fail(what, 'field "base_version" must be a number or null');
  }
  return {
    valid: bool(o, 'valid', what),
    release_blocked: bool(o, 'release_blocked', what),
    problems: problems.map((raw) => parseProfileProblemReport(raw)),
    diff: diff.map((raw) => parseProfileDiffEntry(raw)),
    base_version: typeof base === 'number' ? base : null,
  };
}

/** `ProfileReadResponse` — the note is required because the two nulls need a sentence with them. */
export function parseProfileRead(value: unknown): ProfileReadResponse {
  const o = asRecord(value, 'ProfileReadResponse');
  const what = 'ProfileReadResponse';
  const version = o.active_version;
  if (version !== null && version !== undefined && typeof version !== 'number') {
    fail(what, 'field "active_version" must be a number or null');
  }
  const document = o.document;
  if (document !== null && document !== undefined && typeof document !== 'object') {
    fail(what, 'field "document" must be an object or null');
  }
  return {
    active_version: typeof version === 'number' ? version : null,
    document:
      document === null || document === undefined
        ? null
        : profileDocument(document, `${what}.document`),
    note: str(o, 'note', what),
  };
}

/** `ProfileActivationAccepted` — the shared answer of the two doors that mint a version. */
export function parseProfileActivationAccepted(value: unknown): ProfileActivationAccepted {
  const o = asRecord(value, 'ProfileActivationAccepted');
  const what = 'ProfileActivationAccepted';
  const previous = o.previous_version;
  if (previous !== null && previous !== undefined && typeof previous !== 'number') {
    fail(what, 'field "previous_version" must be a number or null');
  }
  const rollbackOf = o.rollback_of;
  if (rollbackOf !== null && rollbackOf !== undefined && typeof rollbackOf !== 'number') {
    fail(what, 'field "rollback_of" must be a number or null');
  }
  return {
    active_version: num(o, 'active_version', what),
    previous_version: typeof previous === 'number' ? previous : null,
    rollback_of: typeof rollbackOf === 'number' ? rollbackOf : null,
    note: str(o, 'note', what),
  };
}

/** One `ProfileVersionRow`: no document — `GET /ops/v1/operator-profile` is the only export (§۲:۵۹). */
export function parseProfileVersionRow(value: unknown): ProfileVersionRow {
  const o = asRecord(value, 'ProfileVersionRow');
  const what = 'ProfileVersionRow';
  return {
    version: num(o, 'version', what),
    activated_at: str(o, 'activated_at', what),
    actor: profileActor(o.actor, `${what}.actor`),
    changed_keys: strArr(o, 'changed_keys', what),
  };
}

/** `ProfileVersionListResponse` — the bounded window, newest first, with its own caveat in `note`. */
export function parseProfileVersionList(value: unknown): ProfileVersionListResponse {
  const o = asRecord(value, 'ProfileVersionListResponse');
  const what = 'ProfileVersionListResponse';
  return {
    count: num(o, 'count', what),
    has_more: bool(o, 'has_more', what),
    versions: arr(o, 'versions', what).map((raw) => parseProfileVersionRow(raw)),
    note: str(o, 'note', what),
  };
}

/**
 * `ProfileReadinessResponse` — `release_blocked` is required, never defaulted: a readiness report
 * that silently answered «not blocked» for a deployment with no profile is the exact way a release
 * gets signed off against a lie. The `fleet` rows keep every published key — including
 * `write_authorized: false`, which is the design, not an unfinished flag.
 */
export function parseProfileReadiness(value: unknown): ProfileReadinessResponse {
  const o = asRecord(value, 'ProfileReadinessResponse');
  const what = 'ProfileReadinessResponse';
  const version = o.profile_version;
  if (version !== null && version !== undefined && typeof version !== 'number') {
    fail(what, 'field "profile_version" must be a number or null');
  }
  const environment = o.environment;
  if (environment !== null && environment !== undefined && typeof environment !== 'string') {
    fail(what, 'field "environment" must be a string or null');
  }
  return {
    profile_version: typeof version === 'number' ? version : null,
    environment: typeof environment === 'string' ? environment : null,
    release_blocked: bool(o, 'release_blocked', what),
    unknown_inputs: strArr(o, 'unknown_inputs', what),
    unresolved_dependencies: strArr(o, 'unresolved_dependencies', what),
    fleet: arr(o, 'fleet', what).map((raw) => ({ ...asRecord(raw, `${what}.fleet[]`) })),
    capacity: { ...asRecord(o.capacity, `${what}.capacity`) },
    note: str(o, 'note', what),
  };
}

/* ------------------------------------------------------------------ */
/* fleet jobs — `GET /ops/v1/jobs` (`routers/system.py`, U-16-f)         */
/* ------------------------------------------------------------------ */

/**
 * `JobListItem` — every field is echoed as the server sent it.
 *
 * `status` is NOT narrowed to `JOB_STATUSES` here on purpose: `job_machine.TERMINAL` is the alphabet
 * and it carries `timeout`/`cancelled` beyond the four the v1 contract lists. A closed enum in the
 * parser would fail the read — i.e. hide a real job row — the first time the server moves a job to a
 * word this file has not heard of, which is the opposite of the derived-truth rule (Derived-3).
 */
export function parseJobListItem(value: unknown): JobListRow {
  const o = asRecord(value, 'JobListItem');
  const what = 'JobListItem';
  return {
    job_id: str(o, 'job_id', what),
    device_id: strOrNull(o, 'device_id', what) ?? '',
    action: strOrNull(o, 'action', what) ?? '',
    status: str(o, 'status', what),
    requested_at: strOrNull(o, 'requested_at', what),
    started_at: strOrNull(o, 'started_at', what),
    completed_at: strOrNull(o, 'completed_at', what),
    result: o.result ?? null,
    error: o.error ?? null,
  };
}

/**
 * `JobsListResponse` — `items`, `total`, `next_cursor`.
 *
 * `total` is the store's count for this filter over the whole set, not the page length, so the
 * caller must not recompute it from `items.length` and the page must not phrase it as a fleet size.
 */
export function parseJobsList(value: unknown): JobsListResponse {
  const o = asRecord(value, 'JobsListResponse');
  const what = 'JobsListResponse';
  return {
    items: arr(o, 'items', what).map((raw) => parseJobListItem(raw)),
    total: num(o, 'total', what),
    next_cursor: cursor(o, what),
  };
}

/* ------------------------------------------------------------------ */
/* per-device operations — `routers/operations.py` (U-16-f)              */
/* ------------------------------------------------------------------ */

function boolOrNull(obj: Record<string, unknown>, key: string, what: string): boolean | null {
  const v = obj[key];
  if (v === null || v === undefined) return null;
  if (typeof v !== 'boolean') fail(what, `field "${key}" must be a boolean or null`);
  return v;
}

/**
 * `OperationSlotItem` — the gate's answer for one catalog slot, locked rows included.
 *
 * A parser that dropped `available: false` rows would reproduce B-6's concealment in the client, so
 * the row is passed through whole: `locked_reason`, `provenance`, `path`, `rpc` and `detail` are what
 * the panel shows, and `detail` is the server's own next-step sentence.
 */
export function parseOperationSlotItem(value: unknown): OperationSlotItem {
  const o = asRecord(value, 'OperationSlotItem');
  const what = 'OperationSlotItem';
  return {
    op: str(o, 'op', what),
    kind: str(o, 'kind', what),
    value_type: str(o, 'value_type', what),
    secret: bool(o, 'secret', what),
    root: strOrNull(o, 'root', what) ?? '',
    available: bool(o, 'available', what),
    writable: boolOrNull(o, 'writable', what),
    provenance: strOrNull(o, 'provenance', what),
    locked_reason: strOrNull(o, 'locked_reason', what),
    path: strOrNull(o, 'path', what),
    rpc: strOrNull(o, 'rpc', what),
    captured_at: strOrNull(o, 'captured_at', what),
    detail: strOrNull(o, 'detail', what) ?? '',
  };
}

function parseOperationsManifest(value: unknown): OperationsManifestBlock {
  const o = asRecord(value, 'OperationsManifestBlock');
  const what = 'OperationsManifestBlock';
  return {
    manifest_id: strOrNull(o, 'manifest_id', what),
    manifest_version: num(o, 'manifest_version', what),
    matched_on: str(o, 'matched_on', what),
    ladder_rung: num(o, 'ladder_rung', what),
    data_model_root: strOrNull(o, 'data_model_root', what) ?? '',
    provenance_min: strOrNull(o, 'provenance_min', what) ?? '',
  };
}

/** `OperationsResponse` — the slot page plus the manifest block that answered for it. */
export function parseOperationsResponse(value: unknown): OperationsResponse {
  const o = asRecord(value, 'OperationsResponse');
  const what = 'OperationsResponse';
  return {
    items: arr(o, 'items', what).map((raw) => parseOperationSlotItem(raw)),
    manifest: parseOperationsManifest(o.manifest ?? {}),
    next_cursor: cursor(o, what),
  };
}

function parseValuePathEntry(value: unknown): OperationValuePathEntry {
  const o = asRecord(value, 'ValuePathEntry');
  const what = 'ValuePathEntry';
  return {
    root: str(o, 'root', what),
    path: str(o, 'path', what),
    value: o.value ?? null,
    captured_at: strOrNull(o, 'captured_at', what),
    fault_code: strOrNull(o, 'fault_code', what),
  };
}

/**
 * `OperationValueResponse` — one slot's value plus the provenance of the read.
 *
 * `source` is required: it is the field that says where the number came from (`snapshot`,
 * `acs-direct`, `olt-snmp`, `none`), and a payload without it would let the panel present a cached
 * copy as a fresh reading. A secret's value is masked server-side, so `value_length` is the only
 * length the console may show and the content never arrives.
 */
export function parseOperationValue(value: unknown): OperationValueResponse {
  const o = asRecord(value, 'OperationValueResponse');
  const what = 'OperationValueResponse';
  return {
    op: str(o, 'op', what),
    kind: str(o, 'kind', what),
    value: o.value ?? null,
    value_type: str(o, 'value_type', what),
    secret: bool(o, 'secret', what),
    stale: bool(o, 'stale', what),
    value_length: numOrNull(o, 'value_length', what),
    source: str(o, 'source', what),
    captured_at: strOrNull(o, 'captured_at', what),
    provenance: strOrNull(o, 'provenance', what),
    manifest_id: strOrNull(o, 'manifest_id', what),
    manifest_version: num(o, 'manifest_version', what),
    available: bool(o, 'available', what),
    locked_reason: strOrNull(o, 'locked_reason', what),
    paths: arr(o, 'paths', what).map((raw) => parseValuePathEntry(raw)),
    detail: strOrNull(o, 'detail', what) ?? '',
  };
}

/**
 * `OperationWriteAccepted` / `OperationRunAccepted` — the `202` bodies, verbatim.
 *
 * `job_id` and `status` are the whole of what these doors assert; the run door additionally echoes
 * `rpc` and `op` so an operator can see which method left. There is no `verdict`/`applied` field on
 * the wire and this parser would refuse one rather than let it through into a success sentence.
 */
export function parseOperationWriteAccepted(value: unknown): OperationWriteAccepted {
  const o = asRecord(value, 'OperationWriteAccepted');
  const what = 'OperationWriteAccepted';
  return { job_id: str(o, 'job_id', what), status: str(o, 'status', what) };
}

export function parseOperationRunAccepted(value: unknown): OperationRunAccepted {
  const o = asRecord(value, 'OperationRunAccepted');
  const what = 'OperationRunAccepted';
  return {
    job_id: str(o, 'job_id', what),
    status: str(o, 'status', what),
    rpc: strOrNull(o, 'rpc', what) ?? '',
    op: strOrNull(o, 'op', what) ?? '',
  };
}

/* ------------------------------------------------------------------ U-16-g1: users + settings --- */

/**
 * `models.py::ConsoleUser` — the only user shape a door may publish.
 *
 * `role` is nullable because the model says so (`role: str | None`), and the parser asserts the field is
 * simply ABSENT rather than allowed: a row that ever carried `password_hash`, `salt` or `password_kdf`
 * would be rejected here (AGENTS invariant ۴), because the store's own secret fields must not reach a
 * browser even by accident — `identity.py::public_user` is the door's guarantee and this is the console's.
 */
export function parseConsoleUser(value: unknown): ConsoleUser {
  const o = asRecord(value, 'ConsoleUser');
  const what = 'ConsoleUser';
  for (const secret of ['password_hash', 'salt', 'password_kdf']) {
    if (o[secret] !== undefined) fail(what, `field "${secret}" must never leave the store`);
  }
  return {
    user_id: str(o, 'user_id', what),
    username: str(o, 'username', what),
    role: strOrNull(o, 'role', what),
    status: oneOf(o, 'status', USER_STATUSES, what),
    created_at: strOrNull(o, 'created_at', what),
    updated_at: strOrNull(o, 'updated_at', what),
  };
}

/** `200` from `GET /ops/v1/users`: the whole table and its `count` (the door pages nothing). */
export function parseUserList(value: unknown): UserListResponse {
  const o = asRecord(value, 'UserListResponse');
  const users = arr(o, 'users', 'UserListResponse').map(parseConsoleUser);
  return { count: num(o, 'count', 'UserListResponse'), users };
}

/** `200` from the three user-writing doors: the row as it now stands plus the door's own sentence. */
export function parseUserMutationAccepted(value: unknown): UserMutationAccepted {
  const o = asRecord(value, 'UserMutationAccepted');
  return {
    user: parseConsoleUser(o.user),
    detail: str(o, 'detail', 'UserMutationAccepted'),
  };
}

/**
 * A whitelisted settings map: every value an integer, and the key sets of `values` and `defaults` equal.
 *
 * The equality is the door's own relation (`system.py::_settings_read` builds both from the same
 * `OPS_SETTING_DEFAULTS`), asserted instead of counted: a payload whose `values` carries a key its
 * `defaults` lacks would make the form render an editable field no whitelist recognises, and a name
 * list here would be a second copy of the whitelist the console does not own.
 */
function settingsMap(
  obj: Record<string, unknown>,
  key: string,
  what: string,
): Record<string, number> {
  const raw = asRecord(obj[key], `${what}.${key}`);
  const out: Record<string, number> = {};
  for (const [name, value] of Object.entries(raw)) {
    if (typeof value !== 'number' || !Number.isFinite(value) || !Number.isInteger(value)) {
      fail(what, `${key}.${name} must be an integer (the door's whitelist is int-only)`);
    }
    out[name] = value;
  }
  return out;
}

/** `200` from `GET`/`PUT /ops/v1/settings` — and `note` is part of the contract, not decoration. */
export function parseOpsSettingsRead(value: unknown): OpsSettingsRead {
  const o = asRecord(value, 'OpsSettingsRead');
  const what = 'OpsSettingsRead';
  const values = settingsMap(o, 'values', what);
  const defaults = settingsMap(o, 'defaults', what);
  const valueKeys = Object.keys(values).sort();
  const defaultKeys = Object.keys(defaults).sort();
  if (valueKeys.join('|') !== defaultKeys.join('|')) {
    fail(
      what,
      'values and defaults must carry the same whitelisted keys — the door builds both from one table',
    );
  }
  return {
    values,
    defaults,
    updated_at: strOrNull(o, 'updated_at', what),
    note: str(o, 'note', what),
  };
}

/* ------------------------------------------------------------------ */
/* permission matrix — `GET`/`PUT /ops/v1/permissions` (U-16-g2)        */
/* ------------------------------------------------------------------ */

/**
 * capability → role → level, as `models.py::PermissionMatrix.capabilities` publishes it.
 *
 * The completeness rule is enforced HERE at the wire edge rather than in the editor: every key must
 * answer with every published role, and every cell must be a string. §۸-۱ makes an empty cell a red
 * test, and a parse that let a missing cell through would move that red test into a component — where
 * a `undefined` level would render as a blank cell instead of failing loudly. This is a SHAPE check
 * (does the answer match the door's own promise), never an access decision (§۹: the console mirrors).
 */
function matrixMap(
  obj: Record<string, unknown>,
  key: string,
  roles: readonly string[] | null,
  what: string,
) {
  const raw = asRecord(obj[key], `${what}.${key}`);
  const out: Record<string, Record<string, string>> = {};
  for (const [capability, row] of Object.entries(raw)) {
    const cells = asRecord(row, `${what}.${key}.${capability}`);
    const rowRoles = roles ?? Object.keys(cells);
    if (rowRoles.length === 0) {
      fail(
        what,
        `${key}.${capability} carries no role at all — the door's grid is capability × the published roles`,
      );
    }
    const levels: Record<string, string> = {};
    for (const role of rowRoles) {
      const level = cells[role];
      if (typeof level !== 'string' || level.length === 0) {
        fail(
          what,
          `${key}.${capability} is missing the cell for role ${role} — the door publishes every key complete (§۳-۴)`,
        );
      }
      levels[role] = level as string;
    }
    out[capability] = levels;
  }
  return out;
}

/** `200` from `GET /ops/v1/permissions` — the grid plus the vocabulary it was written in. */
export function parsePermissionMatrix(value: unknown): PermissionMatrixRead {
  const o = asRecord(value, 'PermissionMatrixRead');
  const what = 'PermissionMatrixRead';
  const roles = strArr(o, 'roles', what);
  const levels = strArr(o, 'levels', what);
  if (levels.length === 0) {
    fail(
      what,
      "levels must carry the door's alphabet — the editor has no second copy to fall back to",
    );
  }
  return {
    capabilities: matrixMap(o, 'capabilities', roles, what),
    roles,
    levels,
  };
}

/** One `changed` entry of a matrix write — the same four strings the audit row records (§۶). */
function parseMatrixCellChange(value: unknown): MatrixCellChange {
  const o = asRecord(value, 'MatrixCellChange');
  const what = 'MatrixCellChange';
  return {
    capability: str(o, 'capability', what),
    role: str(o, 'role', what),
    before: str(o, 'before', what),
    after: str(o, 'after', what),
  };
}

/** `200` from `PUT /ops/v1/permissions` — what moved, the matrix as it now stands, and the door's sentence. */
export function parseMatrixMutationAccepted(value: unknown): MatrixMutationAccepted {
  const o = asRecord(value, 'MatrixMutationAccepted');
  const what = 'MatrixMutationAccepted';
  // The accepted body carries no `roles` list of its own (`models.py::MatrixMutationAccepted`:
  // changed + capabilities + detail), so this parse reads the grid as published rather than
  // re-checking completeness against an alphabet only the GET carries.
  return {
    changed: arr(o, 'changed', what).map(parseMatrixCellChange),
    capabilities: matrixMap(o, 'capabilities', null, what),
    detail: str(o, 'detail', what),
  };
}

/* ------------------------------------------------------------------ */
/* System measurement (O4) — `GET /ops/v1/system/components` and      */
/* `GET /ops/v1/system/queues`                                       */
/* ------------------------------------------------------------------ */

/**
 * One depth map — the closed vocabulary the façade counted, as numbers.
 *
 * The server sends the WHOLE vocabulary every time it measures, so a word it
 * did not measure is MISSING from the map rather than zero-filled here. This
 * function never invents a word, and never turns a missing word into a zero:
 * a depth the server did not send is not a depth of zero.
 */
function depthMap(obj: Record<string, unknown>, key: string, what: string): Record<string, number> {
  const record = asRecord(obj[key], `${what}.${key}`);
  const out: Record<string, number> = {};
  for (const [word, count] of Object.entries(record)) {
    if (typeof count !== 'number' || !Number.isFinite(count)) {
      fail(what, `depth "${word}" must be a number`);
    }
    out[word] = count;
  }
  return out;
}

/**
 * One component's evidence.
 *
 * The detail is a FREE object on the wire (the backend name, the counts, the
 * mode, the probe error), so unknown keys are kept verbatim instead of being
 * dropped — but the two keys the console reads are type-checked, because a
 * malformed depths object must fail loudly rather than be coerced into zeros.
 */
function componentDetail(value: unknown, what: string): SystemComponentDetail {
  const record = asRecord(value, what);
  const depths = record.depths;
  if (depths !== undefined && depths !== null) {
    const d = asRecord(depths, `${what}.depths`);
    depthMap(d, 'outbox', `${what}.depths`);
    depthMap(d, 'jobs', `${what}.depths`);
  }
  const counts = record.counts;
  if (counts !== undefined && counts !== null) {
    const c = asRecord(counts, `${what}.counts`);
    for (const [key, count] of Object.entries(c)) {
      if (typeof count !== 'number' || !Number.isFinite(count)) {
        fail(what, `count "${key}" must be a number`);
      }
    }
  }
  return record as SystemComponentDetail;
}

/**
 * `GET /ops/v1/system/components` — the four components and their evidence.
 *
 * The store's detail also carries its queue `depths`, and those are `null`
 * when the façade could not measure them: the console must not show a depth
 * the server did not send as a zero.
 */
export function parseSystemComponents(value: unknown): SystemComponentsResponse {
  const o = asRecord(value, 'SystemComponentsResponse');
  const what = 'SystemComponentsResponse';
  const components: Record<string, SystemComponent> = {};
  const raw = asRecord(o.components, `${what}.components`);
  for (const [name, entry] of Object.entries(raw)) {
    const c = asRecord(entry, `${what}.components.${name}`);
    components[name] = {
      status: oneOf(c, 'status', ['up', 'down'], `${what}.components.${name}`) as 'up' | 'down',
      detail: componentDetail(c.detail, `${what}.components.${name}.detail`),
    };
  }
  return {
    service: oneOf(o, 'service', ['acs-api'], what) as 'acs-api',
    status: oneOf(o, 'status', ['ok', 'degraded'], what) as 'ok' | 'degraded',
    components,
    contract_version: oneOf(o, 'contract_version', ['v1'], what) as 'v1',
    version: str(o, 'version', what),
    time: str(o, 'time', what),
  };
}

/**
 * `GET /ops/v1/system/queues` — the depths and the live session count.
 *
 * `outbox` and `jobs` are the closed vocabularies, zero-filled by the server
 * for a word nobody holds. A word the server did not send is missing from
 * the map here, and this parser does not add it.
 */
export function parseSystemQueues(value: unknown): SystemQueuesResponse {
  const o = asRecord(value, 'SystemQueuesResponse');
  const what = 'SystemQueuesResponse';
  return {
    outbox: depthMap(o, 'outbox', what),
    jobs: depthMap(o, 'jobs', what),
    sessions_active: num(o, 'sessions_active', what),
    contract_version: oneOf(o, 'contract_version', ['v1'], what) as 'v1',
    time: str(o, 'time', what),
    note: str(o, 'note', what),
  };
}

/* ---- the workers door (DA-09, wired to a screen by TLM-4b) ----
   THE PAGE DOES NOT JUDGE A WORKER. `status` arrives already labelled by `judge_workers()` —
   the same function the `/metrics` scrape calls — and this layer passes the word through. A
   console that re-derived staleness from `age_seconds` would disagree with the exporter the
   first time either constant moved, and the operator would have two answers to one question.

   `age_seconds` is therefore read as what it is: a number, or `null` meaning «the stored
   instant could not be parsed». A NEGATIVE age is refused rather than clamped — the server
   clamps at zero on the way out, so a negative here is a payload from a different contract,
   and «0 seconds ago» is exactly the invention the null exists to avoid. */

/** A span of seconds the door measured: `null` is «not measurable», a negative is a lie. */
function secondsOrNull(obj: Record<string, unknown>, key: string, what: string): number | null {
  const value = numOrNull(obj, key, what);
  if (value !== null && value < 0) {
    fail(what, `field "${key}" is a span of seconds and cannot be negative, got ${value}`);
  }
  return value;
}

/** One worker's heartbeat row, with the server's judgement kept as the server sent it. */
export function parseSystemWorkerBeat(value: unknown): SystemWorkerBeat {
  const o = asRecord(value, 'SystemWorkerBeat');
  const what = 'SystemWorkerBeat';
  return {
    worker: str(o, 'worker', what),
    status: oneOf(o, 'status', ['ok', 'failing', 'stale'], what) as WorkerBeatState,
    owner: str(o, 'owner', what),
    at: str(o, 'at', what),
    age_seconds: secondsOrNull(o, 'age_seconds', what),
    interval_seconds: secondsOrNull(o, 'interval_seconds', what),
    stale_after_seconds: secondsOrNull(o, 'stale_after_seconds', what),
    beats: num(o, 'beats', what),
    failures: num(o, 'failures', what),
    detail: asRecord(o.detail ?? {}, `${what}.detail`),
  };
}

/**
 * `GET /ops/v1/system/workers` — the heartbeat table, the rollup, and the error.
 *
 * `workers` keeps the ABSENCES: a name the door did not send is not in this map, and this
 * parser does not add a row saying `down` for it. That is the whole shape of the answer —
 * the door cannot know whether the deployment runs a worker it has never heard from.
 */
export function parseSystemWorkers(value: unknown): SystemWorkersResponse {
  const o = asRecord(value, 'SystemWorkersResponse');
  const what = 'SystemWorkersResponse';
  const raw = asRecord(o.workers ?? {}, `${what}.workers`);
  const workers: Record<string, SystemWorkerBeat> = {};
  for (const [name, row] of Object.entries(raw)) {
    const beat = parseSystemWorkerBeat(row);
    workers[name] = beat;
  }
  return {
    service: oneOf(o, 'service', ['acs-api'], what) as 'acs-api',
    status: oneOf(o, 'status', ['ok', 'degraded'], what) as 'ok' | 'degraded',
    workers,
    note: str(o, 'note', what),
    error: str(o, 'error', what),
    contract_version: oneOf(o, 'contract_version', ['v1'], what) as 'v1',
    version: str(o, 'version', what),
    time: str(o, 'time', what),
  };
}

/* ---- the subscriber surface (S1, G-SUB-01/03) ----
   The parsers here do NOT mask and do NOT un-mask. The mask is the SERVER's
   decision — the caller's level on `subscriber.read` decides which fields come
   out of the door, and it is applied on the way out of the façade. A console
   that masked again would turn a whole phone number into stars for a caller
   the server deliberately showed it to, and a console that un-masked would
   publish a phone number the server masked on purpose. So the parser takes the
   answer as it is: a masked value is a string with stars in it, and `null` is
   `null` («this subscriber has no contract number» is not a secret). */

/** One subscriber row, exactly as the door sent it. */
export function parseSubscriber(value: unknown): Subscriber {
  const o = asRecord(value, 'Subscriber');
  const what = 'Subscriber';
  return {
    subscriber_id: str(o, 'subscriber_id', what),
    mobile: str(o, 'mobile', what),
    national_id: strOrNull(o, 'national_id', what),
    contract_number: strOrNull(o, 'contract_number', what),
    status: str(o, 'status', what),
    acs_device_id: strOrNull(o, 'acs_device_id', what),
    service_profile_id: strOrNull(o, 'service_profile_id', what),
    bss_subscriber_ref: strOrNull(o, 'bss_subscriber_ref', what),
    created_at: str(o, 'created_at', what),
    updated_at: str(o, 'updated_at', what),
  };
}

/** The subscriber page: the rows, and the number BEHIND the page. */
export function parseSubscriberList(value: unknown): SubscriberListResponse {
  const o = asRecord(value, 'SubscriberListResponse');
  const what = 'SubscriberListResponse';
  return {
    items: arr(o, 'items', what).map((raw) => parseSubscriber(raw)),
    total: num(o, 'total', what),
    limit: num(o, 'limit', what),
    offset: num(o, 'offset', what),
  };
}

/** The DBA block: the container and its two OPTIONAL ceilings. */
export function parseServiceProfileDba(value: unknown): ServiceProfileDba {
  const o = asRecord(value, 'ServiceProfileDba');
  const what = 'ServiceProfileDba';
  return {
    tcont: str(o, 'tcont', what),
    assured_mbps: numOrNull(o, 'assured_mbps', what),
    maximum_mbps: numOrNull(o, 'maximum_mbps', what),
  };
}

/** One service-profile row — no mask, no level: a profile is not a person. */
export function parseServiceProfile(value: unknown): ServiceProfile {
  const o = asRecord(value, 'ServiceProfile');
  const what = 'ServiceProfile';
  return {
    profile_id: str(o, 'profile_id', what),
    name: str(o, 'name', what),
    down_mbps: num(o, 'down_mbps', what),
    up_mbps: num(o, 'up_mbps', what),
    vlan_id: num(o, 'vlan_id', what),
    dba: parseServiceProfileDba(o.dba),
    created_at: str(o, 'created_at', what),
    updated_at: str(o, 'updated_at', what),
  };
}

/** The service-profile page: the rows, and the number BEHIND the page. */
export function parseServiceProfileList(value: unknown): ServiceProfileListResponse {
  const o = asRecord(value, 'ServiceProfileListResponse');
  const what = 'ServiceProfileListResponse';
  return {
    items: arr(o, 'items', what).map((raw) => parseServiceProfile(raw)),
    total: num(o, 'total', what),
    limit: num(o, 'limit', what),
    offset: num(o, 'offset', what),
  };
}

/* ---- the subscriber search + the 360-degree view (S2/S3, G-SUB-08 / G-PRC-14) ----
   The same house rule as the rest of this file, and it is the rule that matters here:
   a parser REJECTS a shape the server would not send, and a MISSING FIELD IS NOT A
   ZERO.

   **THE PARSERS DO NOT MASK AND DO NOT UN-MASK.** The mask is the DOOR's decision —
   the caller's level on the door's own key decides which fields come out of it. A
   console that masked again would turn a whole phone number into stars for a caller
   the server deliberately showed it to, and a console that un-masked would publish a
   phone number the server masked on purpose. So a masked value is a string with stars
   in it, and `null` is `null` («this subscriber has no contract number» is not a
   secret).

   **A MISSING `online` IS NOT `false`.** The device row carries the flag, the door
   always sends it, and a body without it is a body the server did not write — so the
   parser refuses it instead of inventing an «offline» the door never said. The same
   holds for `binding_version`: a binding row without its version is not a binding at
   version zero. */

/** One row the search matched, exactly as the door sent it. */
export function parseSubscriberSearchHit(value: unknown): SubscriberSearchHit {
  const o = asRecord(value, 'SubscriberSearchHit');
  const what = 'SubscriberSearchHit';
  return {
    subscriber_id: str(o, 'subscriber_id', what),
    mobile: str(o, 'mobile', what),
    national_id: strOrNull(o, 'national_id', what),
    contract_number: strOrNull(o, 'contract_number', what),
    status: str(o, 'status', what),
    acs_device_id: strOrNull(o, 'acs_device_id', what),
    service_profile_id: strOrNull(o, 'service_profile_id', what),
    // The BSS's OWN reference is an IDENTIFIER: the door never masks it, and neither
    // does this parser — it takes the answer as it came.
    bss_subscriber_ref: strOrNull(o, 'bss_subscriber_ref', what),
    created_at: str(o, 'created_at', what),
    updated_at: str(o, 'updated_at', what),
  };
}

/** The search's page: the rows, and the number BEHIND the page. */
export function parseSubscriberSearchResponse(value: unknown): SubscriberSearchResponse {
  const o = asRecord(value, 'SubscriberSearchResponse');
  const what = 'SubscriberSearchResponse';
  return {
    items: arr(o, 'items', what).map((raw) => parseSubscriberSearchHit(raw)),
    total: num(o, 'total', what),
    limit: num(o, 'limit', what),
  };
}

/** The DEVICE row of the 360-degree view — every field is a field of the row. */
export function parseSubscriberDeviceView(value: unknown): SubscriberDeviceView {
  const o = asRecord(value, 'SubscriberDeviceView');
  const what = 'SubscriberDeviceView';
  return {
    acs_device_id: str(o, 'acs_device_id', what),
    serial: strOrNull(o, 'serial', what),
    oui: strOrNull(o, 'oui', what),
    product_class: strOrNull(o, 'product_class', what),
    manufacturer: strOrNull(o, 'manufacturer', what),
    software_version: strOrNull(o, 'software_version', what),
    // The flag is READ, never defaulted: a body without it is a body the door did not
    // write, and «the device is offline» is a claim about the world.
    online: bool(o, 'online', what),
    last_inform_at: strOrNull(o, 'last_inform_at', what),
  };
}

/** The BINDING row of the 360-degree view — the mirror of the claim. */
export function parseSubscriberBindingView(value: unknown): SubscriberBindingView {
  const o = asRecord(value, 'SubscriberBindingView');
  const what = 'SubscriberBindingView';
  return {
    acs_device_id: str(o, 'acs_device_id', what),
    nas_ref: strOrNull(o, 'nas_ref', what),
    subscriber_ref: strOrNull(o, 'subscriber_ref', what),
    // A binding row without its version is not a binding at version zero.
    binding_version: num(o, 'binding_version', what),
    bound_at: strOrNull(o, 'bound_at', what),
  };
}

/**
 * A section of the 360-degree view: PRESENT (an object) or ABSENT (`null`).
 *
 * The key must be there: the door always sends the section, and a section whose row
 * does not exist is `null` — never a missing key and never an invented object.
 */
function nullableSection(
  obj: Record<string, unknown>,
  key: string,
  what: string,
): Record<string, unknown> | null {
  if (!(key in obj)) fail(what, `field "${key}" must be present (null or an object)`);
  const v = obj[key];
  if (v === null) return null;
  return asRecord(v, `${what}.${key}`);
}

/** The 360-degree view of one subscriber — six sections, each naming its own source. */
export function parseSubscriberOverview(value: unknown): SubscriberOverview {
  const o = asRecord(value, 'SubscriberOverview');
  const what = 'SubscriberOverview';
  const device = nullableSection(o, 'device', what);
  const binding = nullableSection(o, 'binding', what);
  const profile = nullableSection(o, 'service_profile', what);
  return {
    subscriber: parseSubscriber(o.subscriber),
    device: device === null ? null : parseSubscriberDeviceView(device),
    binding: binding === null ? null : parseSubscriberBindingView(binding),
    service_profile: profile === null ? null : parseServiceProfile(profile),
    // The two bounded pages: the alarm row is the alarm feed's own row, and the job
    // row is the fleet job list's own row.
    alarms: arr(o, 'alarms', what).map((raw) => parseAlarm(raw)),
    jobs: arr(o, 'jobs', what).map((raw) => parseJobListItem(raw)),
  };
}

/** The provisioning-template list — documents pass through verbatim, the console reads `name`. */
export function parseTemplateList(value: unknown): TemplateListResponse {
  const o = asRecord(value, 'TemplateListResponse');
  const what = 'TemplateListResponse';
  return {
    items: arr(o, 'items', what).map((raw) => asRecord(raw, `${what}.items[]`)),
    total: num(o, 'total', what),
    note: o.note === null || o.note === undefined ? null : str(o, 'note', what),
  };
}

/** The preset write answer — the document echoed verbatim, never a created/replaced claim. */
export function parsePresetWrite(value: unknown): PresetWriteResponse {
  const o = asRecord(value, 'PresetWriteResponse');
  const what = 'PresetWriteResponse';
  return {
    name: str(o, 'name', what),
    document: asRecord(o.document, `${what}.document`),
    note: o.note === null || o.note === undefined ? null : str(o, 'note', what),
  };
}

/** The reconciliation list — runs verbatim, the note always present. */
export function parseReconciliationList(value: unknown): ReconciliationListResponse {
  const o = asRecord(value, 'ReconciliationListResponse');
  const what = 'ReconciliationListResponse';
  const runs = arr(o, 'runs', what).map((raw) => {
    const r = asRecord(raw, `${what}.runs[]`);
    return {
      run_id: str(r, 'run_id', `${what}.runs[].run_id`),
      at: r.at === null || r.at === undefined ? '' : str(r, 'at', `${what}.runs[].at`),
      report: asRecord(r.report, `${what}.runs[].report`),
    };
  });
  return {
    runs,
    total: num(o, 'total', what),
    next_cursor:
      o.next_cursor === null || o.next_cursor === undefined ? null : str(o, 'next_cursor', what),
    note: str(o, 'note', what),
  };
}

/* ------------------------------------------------------------------ */
/* reports — the two doors of `routers/reports.py` (W1)                 */
/* ------------------------------------------------------------------ */

function reportColumn(raw: unknown, what: string): ReportColumn {
  const o = asRecord(raw, what);
  return {
    key: str(o, 'key', what),
    label_fa: str(o, 'label_fa', what),
    kind: oneOf(o, 'kind', REPORT_COLUMN_KINDS, what) as ReportColumn['kind'],
  };
}

function reportFilter(raw: unknown, what: string): ReportFilterDef {
  const o = asRecord(raw, what);
  const kind = oneOf(o, 'kind', REPORT_FILTER_KINDS, what) as ReportFilterDef['kind'];
  const values = strArr(o, 'values', what);
  if ((kind === 'enum') !== values.length > 0) {
    fail(what, `filter "${str(o, 'name', what)}": values belong to enum filters only`);
  }
  return { name: str(o, 'name', what), kind, label_fa: str(o, 'label_fa', what), values };
}

function reportInfo(raw: unknown): ReportInfo {
  const what = 'ReportInfo';
  const o = asRecord(raw, what);
  return {
    report_id: str(o, 'report_id', what),
    title_fa: str(o, 'title_fa', what),
    description_fa: str(o, 'description_fa', what),
    category: str(o, 'category', what),
    source_capability: str(o, 'source_capability', what),
    columns: arr(o, 'columns', what).map((c) => reportColumn(c, `${what}.columns`)),
    filters: arr(o, 'filters', what).map((f) => reportFilter(f, `${what}.filters`)),
    formats: strArr(o, 'formats', what).map((format) => {
      if (!(REPORT_FORMATS as readonly string[]).includes(format)) {
        fail(what, `format "${format}" is not one of ${REPORT_FORMATS.join(', ')}`);
      }
      return format as ReportFormat;
    }),
  };
}

/** `GET /ops/v1/reports`. */
export function parseReportCatalog(value: unknown): ReportCatalog {
  const what = 'ReportCatalog';
  const o = asRecord(value, what);
  return {
    items: arr(o, 'items', what).map((item) => reportInfo(item)),
    max_rows: num(o, 'max_rows', what),
    xlsx_max_rows: num(o, 'xlsx_max_rows', what),
  };
}

/** `GET /ops/v1/reports/{id}?format=json`. */
export function parseReportResult(value: unknown): ReportResult {
  const what = 'ReportResult';
  const o = asRecord(value, what);
  const columns = arr(o, 'columns', what).map((c) => reportColumn(c, `${what}.columns`));
  const rows = arr(o, 'rows', what).map((row, index) => asRecord(row, `${what}.rows[${index}]`));
  const rowCount = num(o, 'row_count', what);
  if (rowCount !== rows.length)
    fail(what, `row_count ${rowCount} but ${rows.length} row(s) arrived`);
  return {
    report_id: str(o, 'report_id', what),
    title_fa: str(o, 'title_fa', what),
    generated_at: str(o, 'generated_at', what),
    filters: asRecord(o.filters ?? {}, `${what}.filters`),
    columns,
    rows,
    row_count: rowCount,
    truncated: bool(o, 'truncated', what),
  };
}

/** The `Content-Disposition` filename — ASCII only by construction, so the plain form is enough. */
function dispositionName(header: string | null, fallback: string): string {
  const match = /filename="?([^";]+)"?/i.exec(header ?? '');
  return match?.[1] ?? fallback;
}

/**
 * A `csv`/`xlsx` report as bytes: the HTTP provider hands this the buffer and the headers, because the
 * facts a download must keep — the file name and the TRUE row count and truncation — travel in headers,
 * and a file the user saves is no place to discover that the export was cut.
 */
export function parseReportDownload(value: unknown, fallbackName: string): ReportDownload {
  const what = 'ReportDownload';
  const o = asRecord(value, what);
  const bytes = o.bytes;
  const headers = o.headers;
  if (!(bytes instanceof ArrayBuffer)) fail(what, 'the body must arrive as bytes');
  if (!(headers instanceof Headers)) fail(what, 'the response headers are missing');
  const h = headers as Headers;
  const rows = Number(h.get('X-Report-Rows'));
  if (!Number.isInteger(rows) || rows < 0) fail(what, 'X-Report-Rows is missing or not a count');
  const truncated = h.get('X-Report-Truncated');
  if (truncated !== 'true' && truncated !== 'false')
    fail(what, 'X-Report-Truncated must be true or false');
  return {
    blob: new Blob([bytes as ArrayBuffer], {
      type: h.get('Content-Type') ?? 'application/octet-stream',
    }),
    fileName: dispositionName(h.get('Content-Disposition'), fallbackName),
    rows,
    truncated: truncated === 'true',
  };
}

/* ------------------------------------------------------------------ */
/* configuration bundles — the five doors of `routers/config.py` (W2)   */
/* ------------------------------------------------------------------ */

/**
 * A bundle is read as a DOCUMENT: the envelope is checked (the console must not offer a restore of
 * something that is not a bundle), the section bodies are kept verbatim. The same function reads an
 * export, a restore point and a file the operator picked from disk — so a hostile or truncated file
 * fails here, in the page, before it reaches a door.
 */
export function parseConfigBundle(value: unknown): ConfigBundle {
  const what = 'ConfigBundle';
  const o = asRecord(value, what);
  const sectionsRaw = asRecord(o.sections, `${what}.sections`);
  const sections: Record<string, Record<string, unknown>> = {};
  for (const [name, body] of Object.entries(sectionsRaw)) {
    sections[name] = asRecord(body, `${what}.sections.${name}`);
  }
  const omittedRaw = asRecord(o.omitted ?? {}, `${what}.omitted`);
  const omitted: Record<string, string> = {};
  for (const [name, reason] of Object.entries(omittedRaw)) {
    if (typeof reason !== 'string') fail(what, `omitted.${name} must be a string`);
    omitted[name] = reason as string;
  }
  const source = o.source === undefined ? undefined : asRecord(o.source, `${what}.source`);
  return {
    format: str(o, 'format', what),
    version: num(o, 'version', what),
    created_at: str(o, 'created_at', what),
    ...(source === undefined
      ? {}
      : {
          source: {
            ...(typeof source.service_version === 'string'
              ? { service_version: source.service_version }
              : {}),
            ...(typeof source.schema_version === 'number'
              ? { schema_version: source.schema_version }
              : {}),
          },
        }),
    sections,
    omitted,
    digest: str(o, 'digest', what),
  };
}

function configProblemLine(raw: unknown, what: string): ConfigProblemLine {
  const o = asRecord(raw, what);
  return { path: str(o, 'path', what), detail: str(o, 'detail', what) };
}

function configSection(raw: unknown, name: string): ConfigSectionReport {
  const what = `ConfigSectionReport.${name}`;
  const o = asRecord(raw, what);
  return {
    status: oneOf(o, 'status', CONFIG_SECTION_STATUSES, what) as ConfigSectionStatus,
    changes: arr(o, 'changes', what).map((entry): ConfigChange => {
      const c = asRecord(entry, `${what}.changes`);
      return { path: str(c, 'path', `${what}.changes`), before: c.before, after: c.after };
    }),
    problems: arr(o, 'problems', what).map((entry) => configProblemLine(entry, `${what}.problems`)),
  };
}

/** `POST /ops/v1/config:validate`. */
export function parseConfigValidation(value: unknown): ConfigValidation {
  const what = 'ConfigValidation';
  const o = asRecord(value, what);
  const sectionsRaw = asRecord(o.sections, `${what}.sections`);
  const sections: Record<string, ConfigSectionReport> = {};
  for (const [name, body] of Object.entries(sectionsRaw))
    sections[name] = configSection(body, name);
  return {
    ok: bool(o, 'ok', what),
    digest: strOrNull(o, 'digest', what),
    problems: arr(o, 'problems', what).map((entry) => configProblemLine(entry, `${what}.problems`)),
    sections,
  };
}

/** `POST /ops/v1/config:apply`. */
export function parseConfigApplied(value: unknown): ConfigApplied {
  const what = 'ConfigApplied';
  const o = asRecord(value, what);
  return {
    digest: strOrNull(o, 'digest', what),
    restore_point_id: strOrNull(o, 'restore_point_id', what),
    applied: strArr(o, 'applied', what),
    unchanged: strArr(o, 'unchanged', what),
    skipped: strArr(o, 'skipped', what),
  };
}

/** `GET /ops/v1/config/restore-points`. */
export function parseConfigRestorePointList(value: unknown): ConfigRestorePointList {
  const what = 'ConfigRestorePointList';
  const o = asRecord(value, what);
  return {
    items: arr(o, 'items', what).map((raw) => {
      const p = asRecord(raw, `${what}.items`);
      return {
        point_id: str(p, 'point_id', what),
        created_at: str(p, 'created_at', what),
        actor: str(p, 'actor', what),
        actor_kind: str(p, 'actor_kind', what),
        sections: strArr(p, 'sections', what),
        digest: str(p, 'digest', what),
      };
    }),
    limit: num(o, 'limit', what),
  };
}

/* ------------------------------------------------------------------ */
/* W5a — four parsers for doors that already exist on the server        */
/* ------------------------------------------------------------------ */

/** `PUT`/`DELETE /ops/v1/devices/{id}/tags/{tag}` — the REMAINING sorted list. */
export function parseTagsResponse(value: unknown): TagsResponse {
  const what = 'TagsResponse';
  const o = asRecord(value, what);
  return { tags: strArr(o, 'tags', what) };
}

/** `POST /ops/v1/devices/{id}:replace` — 202, the activation is still awaiting an Inform. */
export function parseReplaceAccepted(value: unknown): ReplaceAccepted {
  const what = 'ReplaceAccepted';
  const o = asRecord(value, what);
  return {
    activation_id: str(o, 'activation_id', what),
    old_acs_device_id: str(o, 'old_acs_device_id', what),
    status: 'await_inform',
  };
}

/** `POST /ops/v1/devices/{id}:decommission` — no field is paraphrased by the console. */
export function parseDecommissionResponse(value: unknown): DecommissionResponse {
  const what = 'DecommissionResponse';
  const o = asRecord(value, what);
  return {
    decommissioned: true,
    deauthorized: bool(o, 'deauthorized', what),
    binding_removed: bool(o, 'binding_removed', what),
    olt_id: o['olt_id'] === undefined ? null : strOrNull(o, 'olt_id', what),
  };
}

/**
 * `POST /ops/v1/devices/{id}:discover` — the record is VERBATIM. `roots_answered` stays a
 * separate array from `roots_requested`: collapsing the two is exactly the lie the door's
 * own docstring names.
 */
export function parseDiscoverAccepted(value: unknown): DiscoverAccepted {
  const what = 'DiscoverAccepted';
  const o = asRecord(value, what);
  const optionalCount = (key: string): number => (o[key] === undefined ? 0 : num(o, key, what));
  return {
    job_id: str(o, 'job_id', what),
    status: str(o, 'status', what),
    device_id: str(o, 'device_id', what),
    roots_requested: o['roots_requested'] === undefined ? [] : strArr(o, 'roots_requested', what),
    roots_answered: o['roots_answered'] === undefined ? [] : strArr(o, 'roots_answered', what),
    witness_ids: o['witness_ids'] === undefined ? [] : strArr(o, 'witness_ids', what),
    path_count: optionalCount('path_count'),
    gpn_count: optionalCount('gpn_count'),
    gpv_count: optionalCount('gpv_count'),
    truncated: o['truncated'] === undefined ? false : bool(o, 'truncated', what),
    detail: o['detail'] === undefined ? '' : str(o, 'detail', what),
  };
}

/* ------------------------------------------------------------------ */
/* W5c — system maintenance (2026-11-04). The console reads the doors'  */
/* own shapes; every optional field stays optional, and `intact` keeps  */
/* its third state instead of collapsing into a boolean.                */
/* ------------------------------------------------------------------ */

function optionalStr(obj: Record<string, unknown>, key: string, what: string): string | undefined {
  return obj[key] === undefined ? undefined : str(obj, key, what);
}

function optionalStrOrNull(
  obj: Record<string, unknown>,
  key: string,
  what: string,
): string | null | undefined {
  if (obj[key] === undefined) return undefined;
  return strOrNull(obj, key, what);
}

function optionalNum(obj: Record<string, unknown>, key: string, what: string): number | undefined {
  return obj[key] === undefined ? undefined : num(obj, key, what);
}

function optionalNumOrNull(
  obj: Record<string, unknown>,
  key: string,
  what: string,
): number | null | undefined {
  // The contract marks these `nullable: true` and the LIVE adapter answers null (the
  // canned driver does not read them) — null is a reading of "not known", never a
  // parser error.
  if (obj[key] === undefined) return undefined;
  return numOrNull(obj, key, what);
}

function optionalBool(
  obj: Record<string, unknown>,
  key: string,
  what: string,
): boolean | undefined {
  return obj[key] === undefined ? undefined : bool(obj, key, what);
}

export function parseOutboxRow(value: unknown): OutboxRow {
  const what = 'OutboxRow';
  const o = asRecord(value, what);
  return {
    event_id: str(o, 'event_id', what),
    status: oneOf(o, 'status', ['pending', 'sent', 'failed'], what) as OutboxRow['status'],
    attempts: optionalNum(o, 'attempts', what),
    redrives: optionalNum(o, 'redrives', what),
    created_at: optionalStrOrNull(o, 'created_at', what),
    sent_at: optionalStrOrNull(o, 'sent_at', what),
    next_attempt_at: optionalStrOrNull(o, 'next_attempt_at', what),
    last_error: optionalStrOrNull(o, 'last_error', what),
    event:
      o['event'] === undefined || o['event'] === null
        ? undefined
        : asRecord(o['event'], `${what}.event`),
  };
}

export function parseOutboxList(value: unknown): OutboxListResponse {
  const what = 'OutboxListResponse';
  const o = asRecord(value, what);
  return {
    items: arr(o, 'items', what).map((raw) => parseOutboxRow(raw)),
    next_cursor: o['next_cursor'] === undefined ? undefined : cursor(o, what),
    detail: optionalStr(o, 'detail', what),
  };
}

export function parseOutboxRedriveAccepted(value: unknown): OutboxRedriveAccepted {
  const what = 'OutboxRedriveAccepted';
  const o = asRecord(value, what);
  return {
    event_id: str(o, 'event_id', what),
    status: str(o, 'status', what),
    attempts: num(o, 'attempts', what),
    redrives: num(o, 'redrives', what),
    last_error: optionalStrOrNull(o, 'last_error', what),
    detail: optionalStr(o, 'detail', what),
  };
}

function parseMigrationStep(value: unknown): MigrationStep {
  const what = 'MigrationStep';
  const o = asRecord(value, what);
  return {
    version: num(o, 'version', what),
    name: str(o, 'name', what),
    summary: optionalStr(o, 'summary', what),
  };
}

function parseMigrationApplied(value: unknown): MigrationApplied {
  const what = 'MigrationApplied';
  const o = asRecord(value, what);
  return {
    version: num(o, 'version', what),
    name: str(o, 'name', what),
    at: str(o, 'at', what),
    result: optionalStr(o, 'result', what),
  };
}

export function parseMigrationStatus(value: unknown): MigrationStatus {
  const what = 'MigrationStatus';
  const o = asRecord(value, what);
  return {
    recorded: numOrNull(o, 'recorded', what),
    target: num(o, 'target', what),
    applied:
      o['applied'] === undefined ? undefined : arr(o, 'applied', what).map(parseMigrationApplied),
    pending:
      o['pending'] === undefined ? undefined : arr(o, 'pending', what).map(parseMigrationStep),
    detail: optionalStr(o, 'detail', what),
  };
}

export function parseMigrationsApplyAccepted(value: unknown): MigrationsApplyAccepted {
  const what = 'MigrationsApplyAccepted';
  const o = asRecord(value, what);
  return {
    recorded: num(o, 'recorded', what),
    target: num(o, 'target', what),
    applied:
      o['applied'] === undefined ? undefined : arr(o, 'applied', what).map(parseMigrationApplied),
    pending:
      o['pending'] === undefined ? undefined : arr(o, 'pending', what).map(parseMigrationStep),
    detail: optionalStr(o, 'detail', what),
  };
}

export function parseBackupEntry(value: unknown): BackupEntry {
  const what = 'BackupEntry';
  const o = asRecord(value, what);
  return {
    id: str(o, 'id', what),
    kind: oneOf(o, 'kind', ['dump', 'mongodump'], what) as BackupEntry['kind'],
    size_bytes: num(o, 'size_bytes', what),
    modified_at: str(o, 'modified_at', what),
    schema_version: numOrNull(o, 'schema_version', what),
    backend: optionalStrOrNull(o, 'backend', what),
    has_integrity: optionalBool(o, 'has_integrity', what),
  };
}

export function parseBackupList(value: unknown): BackupList {
  const what = 'BackupList';
  const o = asRecord(value, what);
  return {
    count: num(o, 'count', what),
    backups: o['backups'] === undefined ? undefined : arr(o, 'backups', what).map(parseBackupEntry),
    detail: optionalStr(o, 'detail', what),
  };
}

export function parseBackupVerdict(value: unknown): BackupVerdict {
  const what = 'BackupVerdict';
  const o = asRecord(value, what);
  const intactRaw = o['intact'];
  const intact =
    intactRaw === undefined ? undefined : intactRaw === null ? null : bool(o, 'intact', what);
  return {
    id: str(o, 'id', what),
    kind: oneOf(o, 'kind', ['dump', 'mongodump'], what) as BackupEntry['kind'],
    intact,
    reason: optionalStrOrNull(o, 'reason', what),
    schema_version: numOrNull(o, 'schema_version', what),
    size_bytes: optionalNum(o, 'size_bytes', what),
    modified_at: optionalStr(o, 'modified_at', what),
    checks:
      o['checks'] === undefined
        ? undefined
        : (() => {
            const c = asRecord(o['checks'], `${what}.checks`);
            return {
              files_checked: optionalNum(c, 'files_checked', `${what}.checks`),
              mismatches:
                c['mismatches'] === undefined
                  ? undefined
                  : strArr(c, 'mismatches', `${what}.checks`),
            };
          })(),
    detail: optionalStr(o, 'detail', what),
  };
}

export function parseRetentionRunAccepted(value: unknown): RetentionRunAccepted {
  const what = 'RetentionRunAccepted';
  const o = asRecord(value, what);
  return {
    informs_purged: num(o, 'informs_purged', what),
    retention_days: num(o, 'retention_days', what),
    informs_cutoff: optionalStrOrNull(o, 'informs_cutoff', what),
    skipped: optionalStrOrNull(o, 'skipped', what),
    informs_held: optionalBool(o, 'informs_held', what),
    detail: optionalStr(o, 'detail', what),
    outbox_pruned: optionalNum(o, 'outbox_pruned', what),
    outbox_cutoff: optionalStrOrNull(o, 'outbox_cutoff', what),
    outbox_retention_days: optionalNum(o, 'outbox_retention_days', what),
  };
}

export function parseLegalHold(value: unknown): LegalHold {
  const what = 'LegalHold';
  const o = asRecord(value, what);
  return {
    hold_id: str(o, 'hold_id', what),
    scope: oneOf(o, 'scope', ['informs', 'outbox', 'audit', 'all'], what) as LegalHold['scope'],
    reason: str(o, 'reason', what),
    placed_by: str(o, 'placed_by', what),
    placed_at: str(o, 'placed_at', what),
    status: oneOf(o, 'status', ['active', 'lifted'], what) as LegalHold['status'],
    lifted_by: optionalStrOrNull(o, 'lifted_by', what),
    lifted_at: optionalStrOrNull(o, 'lifted_at', what),
    lift_reason: optionalStrOrNull(o, 'lift_reason', what),
  };
}

export function parseLegalHoldList(value: unknown): LegalHoldListResponse {
  const what = 'LegalHoldListResponse';
  const o = asRecord(value, what);
  return {
    items: arr(o, 'items', what).map((raw) => parseLegalHold(raw)),
    total: num(o, 'total', what),
    next_cursor: o['next_cursor'] === undefined ? undefined : cursor(o, what),
  };
}

export function parseLegalHoldWrite(
  value: unknown,
): LegalHoldPlaceResponse | LegalHoldLiftResponse {
  const what = 'LegalHoldWriteResponse';
  const o = asRecord(value, what);
  return {
    hold: parseLegalHold(asRecord(o['hold'], `${what}.hold`)),
    detail: optionalStr(o, 'detail', what),
  };
}
/* W5d — the second factor: the enrollment carries the seed verbatim; the reset carries
   only the counters. Nothing here reshapes or summarizes either one. */
export function parseTotpEnrollment(value: unknown): TotpEnrollment {
  const what = 'TotpEnrollment';
  const o = asRecord(value, what);
  return {
    user_id: str(o, 'user_id', what),
    username: str(o, 'username', what),
    secret_base32: str(o, 'secret_base32', what),
    provisioning_uri: str(o, 'provisioning_uri', what),
    digits: optionalNum(o, 'digits', what),
    period: optionalNum(o, 'period', what),
    algorithm: optionalStr(o, 'algorithm', what),
    sessions_revoked: optionalNum(o, 'sessions_revoked', what),
    detail: optionalStr(o, 'detail', what),
  };
}

export function parseTotpReset(value: unknown): TotpReset {
  const what = 'TotpReset';
  const o = asRecord(value, what);
  return {
    user_id: str(o, 'user_id', what),
    username: str(o, 'username', what),
    sessions_revoked: optionalNum(o, 'sessions_revoked', what),
    detail: optionalStr(o, 'detail', what),
  };
}
/* W5e — the OLT drill-down: every `unknown_fields` list survives into the console (the
   adapter's honesty about what it did not map is part of the answer, not noise). */
function unknownFields(obj: Record<string, unknown>, what: string): string[] | undefined {
  // The contract types these items as plain strings (the LIVE adapter answered exactly
  // that) — an asRecord wrapper here is a parser that rejects the real payload.
  return obj['unknown_fields'] === undefined ? undefined : strArr(obj, 'unknown_fields', what);
}

export function parseOnuDetail(value: unknown): OnuDetailResponse {
  const what = 'OnuDetailResponse';
  const o = asRecord(value, what);
  return {
    olt_id: str(o, 'olt_id', what),
    sn: str(o, 'sn', what),
    pon_port: optionalStrOrNull(o, 'pon_port', what),
    status: optionalStrOrNull(o, 'status', what),
    auth_state: optionalStrOrNull(o, 'auth_state', what),
    uptime_seconds: optionalNumOrNull(o, 'uptime_seconds', what),
    distance: optionalNumOrNull(o, 'distance', what),
    last_down_cause: optionalStrOrNull(o, 'last_down_cause', what),
    note: optionalStrOrNull(o, 'note', what),
    unknown_fields: unknownFields(o, what),
  };
}

function parseMacTableEntry(value: unknown): MacTableEntry {
  const what = 'MacTableEntry';
  const o = asRecord(value, what);
  return {
    mac: optionalStrOrNull(o, 'mac', what),
    ip: optionalStrOrNull(o, 'ip', what),
    vlan: optionalStrOrNull(o, 'vlan', what),
    interface: optionalStrOrNull(o, 'interface', what),
  };
}

export function parseMacTable(value: unknown): MacTableResponse {
  const what = 'MacTableResponse';
  const o = asRecord(value, what);
  return {
    olt_id: str(o, 'olt_id', what),
    sn: str(o, 'sn', what),
    // `entries: null` is the adapter's own "table not known" — carried through as
    // absent; the panel's empty rendering then shows the payload's own note.
    entries:
      o['entries'] === undefined || o['entries'] === null
        ? undefined
        : arr(o, 'entries', what).map(parseMacTableEntry),
    note: optionalStrOrNull(o, 'note', what),
    unknown_fields: unknownFields(o, what),
  };
}

export function parseOnuReboot(value: unknown): OnuRebootResponse {
  const what = 'OnuRebootResponse';
  const o = asRecord(value, what);
  return {
    olt_id: str(o, 'olt_id', what),
    sn: str(o, 'sn', what),
    reboot_issued: bool(o, 'reboot_issued', what),
    issued_at: optionalStrOrNull(o, 'issued_at', what),
  };
}

export function parseOnuBulkAuthorize(value: unknown): OnuBulkAuthorizeResponse {
  const what = 'OnuBulkAuthorizeResponse';
  const o = asRecord(value, what);
  return {
    bulk_id: str(o, 'bulk_id', what),
    olt_id: str(o, 'olt_id', what),
    results:
      o['results'] === undefined
        ? undefined
        : arr(o, 'results', what).map((raw) => {
            const r = asRecord(raw, `${what}.results[]`);
            const result: OnuBulkTargetResult = { sn: str(r, 'sn', `${what}.results[].sn`) };
            if (r['ok'] !== undefined) result.ok = bool(r, 'ok', `${what}.results[]`);
            if (r['code'] !== undefined) result.code = strOrNull(r, 'code', `${what}.results[]`);
            return result;
          }),
  };
}

function parsePonPortRow(value: unknown): PonPortRow {
  const what = 'PonPortRow';
  const o = asRecord(value, what);
  return {
    pon_port: str(o, 'pon_port', what),
    admin_state: optionalStrOrNull(o, 'admin_state', what),
    oper_state: optionalStrOrNull(o, 'oper_state', what),
    onu_count: optionalNum(o, 'onu_count', what),
    unknown_fields: unknownFields(o, what),
  };
}

export function parsePonPorts(value: unknown): PonPortsResponse {
  const what = 'PonPortsResponse';
  const o = asRecord(value, what);
  return {
    olt_id: str(o, 'olt_id', what),
    ports: o['ports'] === undefined ? undefined : arr(o, 'ports', what).map(parsePonPortRow),
    table: optionalStrOrNull(o, 'table', what),
    note: optionalStrOrNull(o, 'note', what),
    unknown_fields: unknownFields(o, what),
  };
}
